import { useState, useEffect, useRef } from "react";
import { X, Settings, KeyRound, Loader2, Save, Plus, Trash2, LayoutDashboard, Cable, Activity, MapPin, Upload, Radar, History, BarChart3, FileText, Library, Router, ChevronDown, Languages, Users, DownloadCloud, RefreshCw, MessageSquare, Keyboard, Monitor } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError, dlToken } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Smartphone } from "lucide-react";
import { WIDGET_KEYS, WIDGET_LABELS } from "@/components/StatsDrawer";
import { Section } from "@/components/settings/Section";
import EmployeePhotos from "@/components/EmployeePhotos";
import { MapSettings, MAP_PROVIDERS } from "@/components/settings/MapSettings";
import { BrandingSettings } from "@/components/settings/BrandingSettings";
import { SystemStatus } from "@/components/settings/SystemStatus";
import { SecretRotation } from "@/components/settings/SecretRotation";
import { LicenseGenerator } from "@/components/settings/LicenseGenerator";
import { PublishRelease } from "@/components/settings/PublishRelease";
import { ActivationCodes } from "@/components/settings/ActivationCodes";
import { LicenseClients } from "@/components/settings/LicenseClients";
import { LicenseMonitor } from "@/components/settings/LicenseMonitor";
import { BackupSettings } from "@/components/settings/BackupSettings";
import { CableColorSettings } from "@/components/settings/CableColorSettings";
import { MarkerIconSettings } from "@/components/settings/MarkerIconSettings";
import { SipSettings } from "@/components/settings/SipSettings";
import { MonterAppCard } from "@/components/settings/MonterAppCard";
import { ClientAppCard } from "@/components/settings/ClientAppCard";
import { NotificationsPanel } from "@/components/settings/NotificationsPanel";
import { WOJEWODZTWA, SMS_PROVIDERS, HW_SMS_PROVIDERS, LICENSE_GROUPS } from "@/components/settings/constants";
import { ShortcutSettings } from "@/components/settings/ShortcutSettings";
import { MenuCustomizer } from "@/components/settings/MenuCustomizer";


export default function SettingsPanel({ currentUser, onClose, onNavigate, initialTab }) {
  const { settings, save } = useSettings();
  const { t } = useI18n();
  const { reload: reloadBranding } = useBranding();
  const { user: authUser, setUser, isMaster } = useAuth();
  const isAdmin = currentUser?.role === "admin";

  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  const TABS = [
    { id: "firma", label: t("Firma i dokumenty"), icon: FileText },
    { id: "cennik", label: t("Cennik i faktury"), icon: BarChart3 },
    { id: "siec", label: t("Sieć"), icon: Router },
    { id: "wyglad", label: t("Wygląd"), icon: LayoutDashboard },
    ...(isMaster ? [{ id: "licencje", label: t("Licencje"), icon: KeyRound }] : []),
    { id: "system", label: t("System"), icon: Settings },
  ];
  const visibleTabs = isAdmin ? TABS : TABS.filter((x) => ["firma", "wyglad", "system"].includes(x.id));
  const [activeTab, setActiveTab] = useState(initialTab || "firma");
  useEffect(() => { if (initialTab) { setActiveTab(initialTab); setOpen("licgen"); } }, [initialTab]);

  // Password change
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  // Technologies + widgets (admin)
  const [techs, setTechs] = useState(settings.client_technologies || []);
  const [newTech, setNewTech] = useState("");
  const [widgets, setWidgets] = useState(settings.dashboard_widgets || []);
  const [budget, setBudget] = useState({
    olt_tx_power_dbm: settings.olt_tx_power_dbm ?? 3,
    min_rx_dbm: settings.min_rx_dbm ?? -28,
    budget_tolerance_db: settings.budget_tolerance_db ?? 3,
    budget_wavelength: settings.budget_wavelength ?? "1310",
    autoplan_price_cable_m: settings.autoplan_price_cable_m ?? 2,
    autoplan_price_closure: settings.autoplan_price_closure ?? 150,
    autoplan_price_splitter: settings.autoplan_price_splitter ?? 80,
  });
  const SPLIT_TYPES = ["1:2", "1:4", "1:8", "1:16", "1:32", "1:64"];
  const DEFAULT_LOSS = { "1:2": 3.5, "1:4": 7.0, "1:8": 10.5, "1:16": 13.5, "1:32": 17.5, "1:64": 20.5 };
  const [splitterLoss, setSplitterLoss] = useState({ ...DEFAULT_LOSS, ...(settings.splitter_loss || {}) });
  const [cfgBusy, setCfgBusy] = useState(false);

  // Serwer aktualizacji + auto-update
  const [upd, setUpd] = useState({ update_server: settings.update_server || "", auto_update_enabled: !!settings.auto_update_enabled, docker_image: settings.docker_image || "", docker_tag: settings.docker_tag || "stable" });
  const [updHist, setUpdHist] = useState([]);
  useEffect(() => { api.get("/update/history").then(({ data }) => setUpdHist(Array.isArray(data) ? data : [])).catch(() => {}); }, []);
  // Klient desktopowy (Windows)
  const [dsRel, setDsRel] = useState(null);
  const [dsVer, setDsVer] = useState("");
  const [dsNotes, setDsNotes] = useState("");
  const [dsSig, setDsSig] = useState("");
  const [dsFile, setDsFile] = useState(null);
  const [dsBusy, setDsBusy] = useState(false);
  const [dsProgress, setDsProgress] = useState(0);
  const loadDsRelease = () => api.get("/admin/desktop/release").then((r) => { setDsRel(r.data); setDsVer((v) => v || r.data?.version || ""); setDsNotes((n) => n || r.data?.notes || ""); }).catch(() => {});
  const DS_CHUNK = 5 * 1024 * 1024;
  const publishInstaller = async () => {
    if (!dsFile) { toast.error(t("Wybierz plik .exe")); return; }
    if (!dsVer.trim()) { toast.error(t("Podaj numer wersji")); return; }
    setDsBusy(true); setDsProgress(0);
    try {
      const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
      const total = Math.ceil(dsFile.size / DS_CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId); fd.append("index", String(i));
        fd.append("file", dsFile.slice(i * DS_CHUNK, (i + 1) * DS_CHUNK));
        await api.post("/admin/desktop/installer/chunk", fd);
        setDsProgress(Math.round(((i + 1) / total) * 100));
      }
      const ff = new FormData();
      ff.append("upload_id", uploadId); ff.append("version", dsVer.trim());
      ff.append("notes", dsNotes); ff.append("filename", dsFile.name); ff.append("signature", dsSig.trim());
      const { data } = await api.post("/admin/desktop/installer/finalize", ff);
      toast.success(`${t("Opublikowano wersję")} ${data.version}`);
      setDsFile(null); loadDsRelease();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || t("Błąd publikacji")); }
    finally { setDsBusy(false); setDsProgress(0); }
  };
  const saveVersionOnly = async () => {
    if (!dsVer.trim()) { toast.error(t("Podaj numer wersji")); return; }
    setDsBusy(true);
    try {
      const ff = new FormData(); ff.append("version", dsVer.trim()); ff.append("notes", dsNotes); ff.append("signature", dsSig.trim());
      const { data } = await api.post("/admin/desktop/installer/finalize", ff);
      toast.success(`${t("Zapisano wersję")} ${data.version}`); loadDsRelease();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || t("Błąd zapisu")); }
    finally { setDsBusy(false); }
  };
  const [updBusy, setUpdBusy] = useState(false);
  const [updInfo, setUpdInfo] = useState(null);
  const saveUpdate = async () => {
    setUpdBusy(true);
    try { await api.put("/settings", { update_server: upd.update_server.trim(), auto_update_enabled: upd.auto_update_enabled, docker_image: upd.docker_image.trim(), docker_tag: (upd.docker_tag || "stable").trim() }); toast.success("Zapisano ustawienia aktualizacji"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUpdBusy(false); }
  };
  const checkUpdate = async () => {
    setUpdBusy(true);
    try { const { data } = await api.get("/update/check"); setUpdInfo(data); (data.update_available ? toast.info(`Dostępna aktualizacja: ${data.latest}`) : toast.success("Masz najnowszą wersję")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUpdBusy(false); }
  };
  const applyUpdate = async () => {
    if (!window.confirm("Uruchomić aktualizację teraz? Aplikacja odświeży się po zakończeniu.")) return;
    setUpdBusy(true);
    try { const { data } = await api.post("/update/apply"); toast.success(data.message || "Aktualizacja uruchomiona"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUpdBusy(false); }
  };
  const [uplBusy, setUplBusy] = useState(false);
  const uploadUpdate = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) { toast.error("Wybierz plik .zip"); e.target.value = ""; return; }
    setUplBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/update/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success(data.message || "Aktualizacja z pliku uruchomiona");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUplBusy(false); e.target.value = ""; }
  };

  // Podgląd czatu (admin)
  const [chatThreads, setChatThreads] = useState([]);
  const [chatSel, setChatSel] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const loadChatThreads = () => api.get("/chat/admin/threads").then((r) => setChatThreads(r.data || [])).catch(() => {});
  const openChatThread = (id) => { setChatSel(id); api.get(`/chat/admin/messages?thread_id=${id}`).then((r) => setChatMsgs(r.data || [])).catch(() => {}); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open === "chatadmin" && isAdmin) loadChatThreads(); if (open === "update" && isAdmin && (upd.update_server || "").trim()) checkUpdate(); if (open === "desktop" && isAdmin) loadDsRelease(); }, [open]);

  // Coverage dictionaries
  const [covMedia, setCovMedia] = useState(settings.coverage_media || []);
  const [covTechs, setCovTechs] = useState(settings.coverage_technologies || []);
  const [smsTpls, setSmsTpls] = useState(settings.sms_reply_templates || []);
  useEffect(() => { setSmsTpls(settings.sms_reply_templates || []); }, [settings.sms_reply_templates]);
  const saveSmsTpls = async () => {
    const cleaned = smsTpls.map((x) => x.trim()).filter(Boolean);
    if (!cleaned.length) { toast.error(t("Dodaj co najmniej jeden szablon przed zapisem")); return; }
    try { await save({ sms_reply_templates: cleaned }); toast.success(t("Zapisano szablony SMS")); }
    catch { toast.error(t("Błąd zapisu")); }
  };
  const [covBw, setCovBw] = useState(settings.coverage_bandwidths || []);
  const [newCov, setNewCov] = useState({ media: "", tech: "", bw: "" });
  const [speedPlans, setSpeedPlans] = useState(settings.speed_plans || []);
  const [newPlan, setNewPlan] = useState("");
  const [fiberStds, setFiberStds] = useState(settings.fiber_standards || []);
  const [defStd, setDefStd] = useState(settings.default_fiber_standard || "");
  const [newStd, setNewStd] = useState({ name: "", colors: "" });
  const [iptvPlans, setIptvPlans] = useState(settings.iptv_plans || []);
  const [voipTariffs, setVoipTariffs] = useState(settings.voip_tariffs || []);
  const [mobilePlans, setMobilePlans] = useState(settings.mobile_plans || []);
  const [iptvChannels, setIptvChannels] = useState(settings.iptv_channels || {});
  const [iptvPdf, setIptvPdf] = useState(settings.iptv_pdf || {});
  const [voipDetails, setVoipDetails] = useState(settings.voip_tariff_details || {});
  const [mobileDetails, setMobileDetails] = useState(settings.mobile_plan_details || {});
  const [newSvc, setNewSvc] = useState({ iptv: "", voip: "", mobile: "" });
  const [portalUrl, setPortalUrl] = useState(settings.portal_url || "");
  const [planPrices, setPlanPrices] = useState(settings.plan_prices || {});
  const [iptvPrices, setIptvPrices] = useState(settings.iptv_prices || {});
  const [voipPrices, setVoipPrices] = useState(settings.voip_prices || {});
  const [mobilePrices, setMobilePrices] = useState(settings.mobile_prices || {});
  const [vatRate, setVatRate] = useState(settings.vat_rate ?? 23);
  const [invoicePrefix, setInvoicePrefix] = useState(settings.invoice_prefix || "FV");
  const [autoInvoice, setAutoInvoice] = useState(!!settings.auto_invoice_monthly);
  const [stests, setStests] = useState(null);
  const [portalCfg, setPortalCfg] = useState({
    router_instructions: settings.router_instructions || "",
    contract_template: settings.contract_template || "",
    rodo_template: settings.rodo_template || "",
  });
  const [company, setCompany] = useState({
    company_name: settings.company_name || "", company_nip: settings.company_nip || "", company_rpt: settings.company_rpt || "",
    company_phone: settings.company_phone || "", company_email: settings.company_email || "",
    company_website: settings.company_website || "",
    company_logo_file_id: settings.company_logo_file_id || "",
  });
  const [logoBusy, setLogoBusy] = useState(false);
  const [mapCfg, setMapCfg] = useState({
    map_provider: settings.map_provider || "osm",
    map_api_key: settings.map_api_key || "",
    map_tile_url: settings.map_tile_url || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    map_tile_attribution: settings.map_tile_attribution || "© OpenStreetMap contributors",
    map_sat_url: settings.map_sat_url || "",
    map_sat_attribution: settings.map_sat_attribution || "",
  });
  const applyMapProvider = (provider, key) => {
    const p = MAP_PROVIDERS[provider];
    if (!p || p.manual) { setMapCfg((c) => ({ ...c, map_provider: provider, map_api_key: key })); return; }
    setMapCfg((c) => ({
      ...c, map_provider: provider, map_api_key: key,
      map_tile_url: p.tile(key), map_tile_attribution: p.tileAttr,
      map_sat_url: p.sat(key), map_sat_attribution: p.satAttr,
    }));
  };
  const [brand, setBrand] = useState({
    brand_name: settings.brand_name || "FiberPass",
    brand_accent: settings.brand_accent || "",
    brand_bg: settings.brand_bg || "",
    brand_theme: settings.brand_theme || "dark",
    license_key: settings.license_key || "",
    eval_watermark: !!settings.eval_watermark,
    eval_watermark_text: settings.eval_watermark_text || "WERSJA EWALUACYJNA",
  });
  const restoreRef = useRef(null);
  const updateRef = useRef(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [backupRunBusy, setBackupRunBusy] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [autoBk, setAutoBk] = useState({
    auto_backup_enabled: !!settings.auto_backup_enabled,
    auto_backup_email: settings.auto_backup_email || "",
    auto_backup_keep: settings.auto_backup_keep || 14,
    require_encrypted_backup: !!settings.require_encrypted_backup,
  });
  const [extBk, setExtBk] = useState({
    backup_s3_enabled: !!settings.backup_s3_enabled,
    backup_s3_endpoint: settings.backup_s3_endpoint || "",
    backup_s3_region: settings.backup_s3_region || "",
    backup_s3_bucket: settings.backup_s3_bucket || "",
    backup_s3_access_key: settings.backup_s3_access_key || "",
    backup_s3_prefix: settings.backup_s3_prefix || "",
    backup_ftp_enabled: !!settings.backup_ftp_enabled,
    backup_ftp_host: settings.backup_ftp_host || "",
    backup_ftp_port: settings.backup_ftp_port || 21,
    backup_ftp_user: settings.backup_ftp_user || "",
    backup_ftp_dir: settings.backup_ftp_dir || "",
    backup_ftp_tls: !!settings.backup_ftp_tls,
  });
  const [s3Secret, setS3Secret] = useState("");
  const [ftpPass, setFtpPass] = useState("");
  const [extTestBusy, setExtTestBusy] = useState(false);
  const testExternal = async () => {
    setExtTestBusy(true);
    try {
      const { data } = await api.post("/admin/backup/test-external");
      const line = (label, v) => {
        if (v === "ok") return `${label}: ✅`;
        if (v === "disabled") return `${label}: ${t("wyłączone")}`;
        return `${label}: ⚠️ ${String(v).replace(/^error:\s*/, "")}`;
      };
      const msg = [line("S3", data.s3), line("FTP", data.ftp)].join("  ·  ");
      if ([data.s3, data.ftp].some((v) => typeof v === "string" && v.startsWith("error"))) toast.error(msg, { duration: 9000 });
      else if ([data.s3, data.ftp].includes("ok")) toast.success(msg);
      else toast.info(t("Najpierw włącz i zapisz cel zewnętrzny (S3 lub FTP)."));
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setExtTestBusy(false); }
  };
  const smtpConfigured = !!settings.smtp_host;
  const [badgeMonths, setBadgeMonths] = useState(settings.badge_validity_months || 12);
  const loadBackups = async () => {
    try { const { data } = await api.get("/admin/backups"); setBackupList(data.backups || []); } catch { /* ignore */ }
  };
  const runBackupNow = async () => {
    setBackupRunBusy(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      toast.success(t("Utworzono kopię") + `: ${data.name}` + (data.emailed ? " " + t("(wysłano e-mailem)") : ""));
      loadBackups();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBackupRunBusy(false); }
  };
  const restoreSaved = async (name) => {
    if (!window.confirm(t("Przywrócić z tej kopii? Nadpisze wszystkie obecne dane."))) return;
    try {
      const { data } = await api.post(`/admin/backups/${encodeURIComponent(name)}/restore`);
      toast.success(t("Przywrócono dane") + `: ${data.collections} kolekcji, ${data.files} plików. Odśwież (Ctrl+F5).`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const deleteSaved = async (name) => {
    if (!window.confirm(t("Usunąć tę kopię zapasową?"))) return;
    try { await api.delete(`/admin/backups/${encodeURIComponent(name)}`); loadBackups(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const downloadSaved = (name) =>
    window.open(`${API}/admin/backups/${encodeURIComponent(name)}?auth=${encodeURIComponent(dlToken())}`, "_blank");
  const downloadBackup = () =>
    window.open(`${API}/admin/backup?auth=${encodeURIComponent(dlToken())}`, "_blank");
  const doRestore = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    if (!window.confirm(t("UWAGA: przywrócenie NADPISZE wszystkie obecne dane. Zalecana wcześniejsza kopia zapasowa. Kontynuować?"))) return;
    let encKey = "";
    if (/\.enc$/i.test(f.name)) {
      const v = window.prompt(t("Kopia jest zaszyfrowana. Wpisz klucz szyfrowania z serwera, na którym ją zrobiono (zostaw puste, jeśli to ten sam serwer):"), "");
      if (v === null) return;
      encKey = v.trim();
    }
    setRestoreBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      if (encKey) fd.append("enc_key", encKey);
      const { data } = await api.post("/admin/restore", fd);
      toast.success(t("Przywrócono dane") + `: ${data.collections} kolekcji, ${data.files} plików. Odśwież stronę (Ctrl+F5).`);
      if (data.key_activated) toast.success(t("Klucz szyfrowania został aktywowany na tym serwerze — kolejne kopie będą szyfrowane tym samym kluczem."));
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setRestoreBusy(false); }
  };
  const doUpdate = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    if (!window.confirm(t("Wgrać i zainstalować aktualizację? Aplikacja przebuduje się i zrestartuje automatycznie (2–5 min)."))) return;
    setUpdateBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/update/upload", fd, { timeout: 300000 });
      toast.success(data.message || t("Aktualizacja uruchomiona."));
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setUpdateBusy(false); }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "settings"); fd.append("entity_id", "global"); fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setCompany((c) => ({ ...c, company_logo_file_id: data.id }));
      toast.success(t("Logo wgrane — kliknij „Zapisz ustawienia”"));
    } catch (err) { toast.error(t("Błąd wgrywania logo")); }
    finally { setLogoBusy(false); e.target.value = ""; }
  };
  const [ai, setAi] = useState({ ai_provider: settings.ai_provider || "openai", ai_model: settings.ai_model || "" });
  const [aiKey, setAiKey] = useState("");
  const [notif, setNotif] = useState({
    notify_email_enabled: !!settings.notify_email_enabled,
    smtp_host: settings.smtp_host || "", smtp_port: settings.smtp_port || 587,
    smtp_user: settings.smtp_user || "", notify_from_email: settings.notify_from_email || "",
    notify_to_emails: settings.notify_to_emails || "",
    notify_sms_enabled: !!settings.notify_sms_enabled, sms_provider: settings.sms_provider || "twilio",
    sms_account_sid: settings.sms_account_sid || "", sms_from: settings.sms_from || "", sms_to: settings.sms_to || "",
    sms_host: settings.sms_host || "", sms_port: settings.sms_port || "", sms_username: settings.sms_username || "",
    sms_line: settings.sms_line || "", sms_use_tls: !!settings.sms_use_tls,
    sms_inbound_enabled: !!settings.sms_inbound_enabled, sms_inbound_keyword: settings.sms_inbound_keyword || "AWARIA", sms_inbound_secret: settings.sms_inbound_secret || "",
    notify_telegram_enabled: !!settings.notify_telegram_enabled, telegram_chat_id: settings.telegram_chat_id || "",
    uplink_alarm_threshold: settings.uplink_alarm_threshold ?? 80,
  });
  const [tgToken, setTgToken] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpTestBusy, setSmtpTestBusy] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const testSmtp = async () => {
    setSmtpTestBusy(true); setSmtpTestResult(null);
    try {
      const { data } = await api.post("/notifications/smtp-test", {});
      setSmtpTestResult(data);
      if (data.ok) toast.success(t("SMTP działa — wysłano test")); else toast.error(t("SMTP: błąd — sprawdź szczegóły"));
    } catch (err) {
      const d = formatApiError(err.response?.data?.detail);
      setSmtpTestResult({ ok: false, detail: d }); toast.error(d);
    } finally { setSmtpTestBusy(false); }
  };
  const [smsToken, setSmsToken] = useState("");
  const [smsBalance, setSmsBalance] = useState(null);
  const [balBusy, setBalBusy] = useState(false);
  const [smsGwBusy, setSmsGwBusy] = useState(false);
  const [smsGwResult, setSmsGwResult] = useState(null);
  const sendSmsGwTest = async () => {
    setSmsGwBusy(true); setSmsGwResult(null);
    try {
      const { data } = await api.post("/notifications/sms-gw-test", {});
      setSmsGwResult(data);
      if (data.ok) toast.success(t("Testowy SMS wysłany przez bramkę"));
      else toast.error(t("Bramka odrzuciła wysyłkę — sprawdź szczegóły"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSmsGwBusy(false); }
  };
  const checkSmsBalance = async () => {
    setBalBusy(true);
    try {
      const { data } = await api.post("/notifications/sms-balance");
      setSmsBalance(data);
      if (data.ok) toast.success(`${t("Saldo")}: ${data.balance ?? "?"} ${data.unit || ""} (${data.provider})`);
      else toast.error(`${t("Nie udało się pobrać salda")}: ${data.detail || "?"}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBalBusy(false); }
  };
  const [net, setNet] = useState({
    snmp_poll_interval: settings.snmp_poll_interval || 60,
    snmp_trap_port: settings.snmp_trap_port || 162,
    snmp_refresh_interval: settings.snmp_refresh_interval || 30,
    snmp_offline_threshold: settings.snmp_offline_threshold || 3,
    nearby_radius_m: settings.nearby_radius_m || 100,
    vlan_id: settings.vlan_id || "", vlan_parent: settings.vlan_parent || "eth0",
    vpn_enabled: !!settings.vpn_enabled, vpn_type: settings.vpn_type || "wireguard",
    vpn_host: settings.vpn_host || "", vpn_port: settings.vpn_port || 51820,
  });
  const [vpnKey, setVpnKey] = useState("");
  const [vpnFileName, setVpnFileName] = useState("");
  const [vpnUsername, setVpnUsername] = useState(settings.vpn_username || "");
  const [vpnPassword, setVpnPassword] = useState("");
  const [vpnStatus, setVpnStatus] = useState(null);
  const [vpnBusy, setVpnBusy] = useState(false);
  const loadVpnStatus = () => api.get("/snmp/vpn/status").then((r) => setVpnStatus(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) loadVpnStatus(); }, [isAdmin]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open === "backup" && isAdmin) loadBackups(); }, [open]);
  const vpnAction = async (action) => {
    setVpnBusy(true);
    try {
      const { data } = await api.post(`/snmp/vpn/${action}`);
      setVpnStatus(data);
      toast.success(action === "connect"
        ? (data.connected ? t("Połączono z VPN") : t("Uruchomiono — sprawdzam status…"))
        : t("Rozłączono VPN"));
      if (action === "connect") setTimeout(loadVpnStatus, 4000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setVpnBusy(false); }
  };
  const [vlanBusy, setVlanBusy] = useState(false);

  const handleVpnFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.find((f) => f.size > 1024 * 1024)) return toast.error(t("Plik jest za duży (maks. 1 MB)"));
    const read = (f) => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res({ name: f.name, text: String(r.result || "") });
      r.onerror = rej;
      r.readAsText(f);
    });
    try {
      const parts = await Promise.all(files.map(read));
      // Pojedynczy plik = traktuj jako gotowy (samodzielny) config .ovpn
      if (files.length === 1) {
        setVpnKey(parts[0].text);
        setVpnFileName(parts[0].name);
        return toast.success(`${t("Wczytano plik")}: ${parts[0].name}`);
      }
      // Wiele plików: scal ca/cert/key w jeden samodzielny .ovpn (bloki inline)
      const cfg = parts.find((p) => /\.(ovpn|conf)$/i.test(p.name));
      const keyF = parts.find((p) => /\.key$/i.test(p.name)) || parts.find((p) => p !== cfg && /PRIVATE KEY/.test(p.text));
      const certs = parts.filter((p) => p !== cfg && p !== keyF && /BEGIN CERTIFICATE/.test(p.text));
      let caF = certs.find((p) => /(^|[^a-z0-9])ca([^a-z0-9]|$)/i.test(p.name));
      let clientF = certs.find((p) => p !== caF);
      if (!caF && certs.length > 1) { clientF = certs[0]; caF = certs[1]; }
      let base = (cfg ? cfg.text : "").split(/\r?\n/)
        .filter((l) => !/^\s*(ca|cert|key|pkcs12)\s+\S/.test(l)).join("\n").trimEnd();
      const block = (tag, f) => (f ? `<${tag}>\n${f.text.replace(/\r/g, "").trim()}\n</${tag}>\n` : "");
      const out = base + "\n" + block("ca", caF) + block("cert", clientF) + block("key", keyF);
      setVpnKey(out);
      setVpnFileName(files.map((f) => f.name).join(", "));
      toast.success(`${t("Scalono pliki VPN w jeden config")}: ${files.length}`);
    } catch (err) {
      toast.error(t("Nie udało się odczytać plików"));
    }
  };

  const applyVlan = async () => {
    if (!net.vlan_id) return toast.error(t("Podaj VLAN ID"));
    setVlanBusy(true);
    try {
      const { data } = await api.post("/snmp/network/apply-vlan", { vlan_id: net.vlan_id, vlan_parent: net.vlan_parent });
      if (data.ok) toast.success(`${t("Utworzono interfejs VLAN")}: ${data.interface}`);
      else toast.error(`${t("Nie udało się utworzyć VLAN")}: ${(data.steps || []).map((s) => s.err).filter(Boolean).join("; ") || "?"}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setVlanBusy(false); }
  };

  const renderChips = (list, setList, val, valKey, placeholder, testid) => {
    const commit = () => {
      const t = String(val).trim();
      if (!t || list.includes(t)) return;
      setList([...list, t]);
      setNewCov((p) => ({ ...p, [valKey]: "" }));
    };
    return (
      <>
        <div className="flex flex-wrap gap-2 mb-2">
          {list.map((t) => (
            <span key={t} data-testid={`${testid}-chip-${t}`} className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-full pl-3 pr-1.5 py-1 text-xs">
              {t}
              <button onClick={() => setList(list.filter((x) => x !== t))} data-testid={`${testid}-remove-${t}`} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>
            </span>
          ))}
          {list.length === 0 && <span className="text-xs text-slate-500">{t("Brak")}</span>}
        </div>
        <div className="flex gap-2">
          <input value={val} onChange={(e) => setNewCov((p) => ({ ...p, [valKey]: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && commit()} placeholder={placeholder} data-testid={`${testid}-input`} className={input} />
          <button onClick={commit} data-testid={`${testid}-add`} className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm"><Plus size={15} /> {t("Dodaj")}</button>
        </div>
      </>
    );
  };

  // Address base
  const [addrFile, setAddrFile] = useState(null);
  const [addrEpsg, setAddrEpsg] = useState("");
  const [addrWoj, setAddrWoj] = useState("");
  const [addrBusy, setAddrBusy] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const loadProvinces = () => api.get("/addresses/provinces").then((r) => setProvinces(r.data)).catch(() => {});
  useEffect(() => { loadProvinces(); }, []);
  const addrTotal = provinces.reduce((s, p) => s + p.count, 0);

  const importAddresses = async () => {
    if (!addrFile) return toast.error(t("Wybierz plik CSV z bazą adresową"));
    if (!addrWoj) return toast.error(t("Wybierz województwo"));
    setAddrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", addrFile);
      fd.append("replace", "true");
      fd.append("wojewodztwo", addrWoj);
      if (addrEpsg) fd.append("epsg", addrEpsg);
      const { data } = await api.post("/addresses/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Zaimportowano ${data.imported} adresów (${addrWoj})`);
      setAddrFile(null); loadProvinces();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAddrBusy(false); }
  };
  const deleteProvince = async (woj) => {
    if (!window.confirm(`Usunąć bazę adresową dla: ${woj}?`)) return;
    try {
      await api.delete("/addresses", { params: { wojewodztwo: woj } });
      toast.success(`${t("Usunięto bazę adresową:")} ${woj}`); loadProvinces();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changePassword = async () => {
    if (!pwd.current_password || !pwd.new_password) return toast.error(t("Uzupełnij oba pola hasła"));
    if (pwd.new_password.length < 6) return toast.error(t("Nowe hasło musi mieć min. 6 znaków"));
    if (pwd.new_password !== pwd.confirm) return toast.error(t("Hasła nie są zgodne"));
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password }); // nowy token ustawia serwer w ciasteczku
      toast.success(t("Hasło zostało zmienione"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPwdBusy(false); }
  };

  const addTech = () => {
    const t = newTech.trim();
    if (!t || techs.includes(t)) return;
    setTechs([...techs, t]);
    setNewTech("");
  };
  const removeTech = (t) => setTechs(techs.filter((x) => x !== t));
  const toggleWidget = (k) => setWidgets((w) => (w.includes(k) ? w.filter((x) => x !== k) : [...w, k]));

  const testNotif = async () => {
    try {
      const { data } = await api.post("/notifications/test");
      const line = (label, v) => {
        if (v === "sent") return `${label}: ✅`;
        if (v === "disabled") return `${label}: ${t("wyłączone")}`;
        if (v === "noconfig") return `${label}: ${t("brak konfiguracji")}`;
        return `${label}: ⚠️ ${String(v).replace(/^error:\s*/, "")}`;
      };
      const vals = [data.email, data.sms, data.telegram];
      const msg = [line("E-mail", data.email), line("SMS", data.sms), line("Telegram", data.telegram)].join("  ·  ");
      if (vals.some((v) => typeof v === "string" && v.startsWith("error"))) toast.error(msg, { duration: 9000 });
      else if (vals.includes("sent")) toast.success(msg);
      else toast.info(t("Najpierw włącz i skonfiguruj kanał (E-mail / SMS / Telegram)."));
    } catch (err) {
      const st = err?.response?.status;
      toast.error(st
        ? `${t("Błąd serwera")} (${st}) — ${t("sprawdź logi backendu")}: journalctl -u fiberpass-backend -n 50`
        : t("Serwer nie odpowiedział (timeout/sieć). Sprawdź, czy backend działa i czy odświeżyłeś build."));
    }
  };

  const saveConfig = async () => {
    setCfgBusy(true);
    try {
      await save({
        client_technologies: techs,
        dashboard_widgets: widgets,
        olt_tx_power_dbm: Number(budget.olt_tx_power_dbm),
        min_rx_dbm: Number(budget.min_rx_dbm),
        budget_tolerance_db: Number(budget.budget_tolerance_db),
        budget_wavelength: String(budget.budget_wavelength),
        autoplan_price_cable_m: Number(budget.autoplan_price_cable_m) || 0,
        autoplan_price_closure: Number(budget.autoplan_price_closure) || 0,
        autoplan_price_splitter: Number(budget.autoplan_price_splitter) || 0,
        splitter_loss: Object.fromEntries(SPLIT_TYPES.map((t) => [t, Number(splitterLoss[t])])),
        coverage_media: covMedia,
        coverage_technologies: covTechs,
        coverage_bandwidths: covBw,
        speed_plans: speedPlans,
        fiber_standards: fiberStds,
        default_fiber_standard: defStd,
        iptv_plans: iptvPlans,
        iptv_channels: iptvChannels,
        iptv_pdf: iptvPdf,
        voip_tariffs: voipTariffs,
        voip_tariff_details: voipDetails,
        mobile_plans: mobilePlans,
        mobile_plan_details: mobileDetails,
        portal_url: portalUrl,
        plan_prices: planPrices,
        iptv_prices: iptvPrices,
        voip_prices: voipPrices,
        mobile_prices: mobilePrices,
        vat_rate: Number(vatRate) || 0,
        invoice_prefix: invoicePrefix,
        auto_invoice_monthly: autoInvoice,
        router_instructions: portalCfg.router_instructions,
        contract_template: portalCfg.contract_template,
        rodo_template: portalCfg.rodo_template,
        company_name: company.company_name,
        company_nip: company.company_nip,
        company_rpt: company.company_rpt,
        company_phone: company.company_phone,
        company_email: company.company_email,
        company_website: company.company_website,
        company_logo_file_id: company.company_logo_file_id,
        map_tile_url: mapCfg.map_tile_url.trim(),
        map_tile_attribution: mapCfg.map_tile_attribution.trim(),
        map_sat_url: mapCfg.map_sat_url.trim(),
        map_sat_attribution: mapCfg.map_sat_attribution.trim(),
        map_provider: mapCfg.map_provider,
        map_api_key: mapCfg.map_api_key.trim(),
        brand_name: brand.brand_name.trim() || "FiberPass",
        brand_accent: brand.brand_accent.trim(),
        brand_bg: brand.brand_bg.trim(),
        eval_watermark: brand.eval_watermark,
        eval_watermark_text: brand.eval_watermark_text.trim() || "WERSJA EWALUACYJNA",
        brand_theme: brand.brand_theme,
        license_key: brand.license_key.trim(),
        auto_backup_enabled: autoBk.auto_backup_enabled,
        auto_backup_email: autoBk.auto_backup_email.trim(),
        auto_backup_keep: Number(autoBk.auto_backup_keep) || 14,
        require_encrypted_backup: autoBk.require_encrypted_backup,
        badge_validity_months: Number(badgeMonths) || 12,
        backup_s3_enabled: extBk.backup_s3_enabled,
        backup_s3_endpoint: extBk.backup_s3_endpoint.trim(),
        backup_s3_region: extBk.backup_s3_region.trim(),
        backup_s3_bucket: extBk.backup_s3_bucket.trim(),
        backup_s3_access_key: extBk.backup_s3_access_key.trim(),
        backup_s3_prefix: extBk.backup_s3_prefix.trim(),
        ...(s3Secret.trim() ? { backup_s3_secret_key: s3Secret.trim() } : {}),
        backup_ftp_enabled: extBk.backup_ftp_enabled,
        backup_ftp_host: extBk.backup_ftp_host.trim(),
        backup_ftp_port: Number(extBk.backup_ftp_port) || 21,
        backup_ftp_user: extBk.backup_ftp_user.trim(),
        backup_ftp_dir: extBk.backup_ftp_dir.trim(),
        backup_ftp_tls: extBk.backup_ftp_tls,
        ...(ftpPass.trim() ? { backup_ftp_pass: ftpPass.trim() } : {}),
        ai_provider: ai.ai_provider,
        ai_model: ai.ai_model,
        ...(aiKey.trim() ? { ai_api_key: aiKey.trim() } : {}),
        notify_email_enabled: notif.notify_email_enabled,
        smtp_host: notif.smtp_host, smtp_port: Number(notif.smtp_port) || 587, smtp_user: notif.smtp_user,
        notify_from_email: notif.notify_from_email, notify_to_emails: notif.notify_to_emails,
        notify_sms_enabled: notif.notify_sms_enabled, sms_provider: notif.sms_provider,
        sms_account_sid: notif.sms_account_sid, sms_from: notif.sms_from, sms_to: notif.sms_to,
        sms_host: notif.sms_host, sms_port: notif.sms_port ? Number(notif.sms_port) : null,
        sms_username: notif.sms_username, sms_line: notif.sms_line, sms_use_tls: notif.sms_use_tls,
        sms_inbound_enabled: notif.sms_inbound_enabled, sms_inbound_keyword: notif.sms_inbound_keyword, sms_inbound_secret: notif.sms_inbound_secret,
        ...(smtpPass.trim() ? { smtp_pass: smtpPass.trim() } : {}),
        ...(smsToken.trim() ? { sms_auth_token: smsToken.trim() } : {}),
        notify_telegram_enabled: notif.notify_telegram_enabled, telegram_chat_id: notif.telegram_chat_id,
        uplink_alarm_threshold: Number(notif.uplink_alarm_threshold) || 80,
        ...(tgToken.trim() ? { telegram_bot_token: tgToken.trim() } : {}),
        snmp_poll_interval: Number(net.snmp_poll_interval) || 60,
        snmp_trap_port: Number(net.snmp_trap_port) || 162,
        snmp_refresh_interval: Number(net.snmp_refresh_interval) || 30,
        snmp_offline_threshold: Number(net.snmp_offline_threshold) || 3,
        nearby_radius_m: Number(net.nearby_radius_m) || 100,
        vlan_id: net.vlan_id ? Number(net.vlan_id) : null, vlan_parent: net.vlan_parent || "eth0",
        vpn_enabled: net.vpn_enabled, vpn_type: net.vpn_type,
        vpn_host: net.vpn_host, vpn_port: Number(net.vpn_port) || 51820,
        ...(vpnKey.trim() ? { vpn_key: vpnKey.trim() } : {}),
        vpn_username: vpnUsername,
        ...(vpnPassword.trim() ? { vpn_password: vpnPassword.trim() } : {}),
      });
      toast.success(t("Zapisano ustawienia"));
      reloadBranding();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCfgBusy(false); }
  };

  const input = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
  const navBtn = "flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-slate-200 hover:border-cyan-600 hover:bg-slate-800 transition-colors";
  const nav = (t) => onNavigate && onNavigate(t);

  const [dlBusy, setDlBusy] = useState(false);
  const dlPackage = async () => {
    setDlBusy(true);
    try {
      const res = await api.get("/download/full-package", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "fiberpass-full.zip";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano paczkę aktualizacyjną (ZIP)"));
    } catch { toast.error(t("Nie udało się pobrać paczki")); }
    finally { setDlBusy(false); }
  };

  const [filmBusy, setFilmBusy] = useState(false);
  const dlFilm = async () => {
    setFilmBusy(true);
    try {
      const res = await api.get("/download/film", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "FiberPass-prezentacja.mp4";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano film prezentacji (MP4)"));
    } catch { toast.error(t("Nie udało się pobrać filmu")); }
    finally { setFilmBusy(false); }
  };

  const smsTokenPlaceholder = () => {
    if (settings.sms_token_set) return t("Klucz/token (ustawiony — puste = bez zmian)");
    return {
      twilio: t("Twilio Auth Token"),
      smsapi: t("Token API (OAuth)"),
      justsend: t("Klucz API (App-Key)"),
      "sms-fly": t("Klucz API"),
      smseagle: t("Hasło API bramki"),
      teltonika: t("Hasło do routera"),
      yeastar: t("Hasło API bramki"),
      goip: t("Hasło do bramki"),
    }[notif.sms_provider] || t("Klucz / token API");
  };
  const isHwSms = HW_SMS_PROVIDERS.includes(notif.sms_provider);
  const smsLinePlaceholder = {
    teltonika: t("Modem (np. 1-1) — opcjonalnie"),
    yeastar: t("Port (np. 1) — opcjonalnie"),
    goip: t("Linia (np. 1) — opcjonalnie"),
    smseagle: t("Modem — opcjonalnie"),
  }[notif.sms_provider] || t("Linia / port / modem (opcjonalnie)");

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="settings-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2"><Settings size={18} className="text-cyan-400" /><h2 className="font-heading font-bold text-lg">{t("Ustawienia")}</h2></div>
        <button onClick={onClose} data-testid="settings-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      {/* Zakładki kategorii ustawień */}
      <div className="px-5 pt-3 border-b border-slate-700 bg-slate-900">
        <div className="max-w-3xl mx-auto flex flex-wrap gap-1" data-testid="settings-tabs">
          {visibleTabs.map((tb) => {
            const Icon = tb.icon;
            const on = activeTab === tb.id;
            return (
              <button key={tb.id} data-testid={`settings-tab-${tb.id}`} onClick={() => { setActiveTab(tb.id); setOpen(null); }}
                className={`flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm border-b-2 transition-colors ${on ? "border-cyan-400 text-cyan-200 bg-slate-800" : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"}`}>
                <Icon size={15} /> {tb.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5 overflow-y-auto flex-1">
        <div className="max-w-3xl mx-auto space-y-3">

          {/* ============ FIRMA I DOKUMENTY ============ */}
          {activeTab === "firma" && (
            <div className="space-y-3">
              <Section icon={Smartphone} iconColor="text-cyan-400" title={t("Aplikacja montera (na telefon)")}
                open={open === "monterapp"} onToggle={() => toggle("monterapp")} testid="settings-sec-monterapp">
                <MonterAppCard t={t} canSend={currentUser?.role === "admin" || currentUser?.role === "projektant"} embedded />
              </Section>

              {isAdmin && (
                <>
                  {/* Dane firmy */}
                  <Section icon={FileText} title={t("Dane firmy (na stronę tytułową PDF)")}
                    open={open === "company"} onToggle={() => toggle("company")} testid="settings-sec-company">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input value={company.company_name} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} placeholder={t("Nazwa firmy")} data-testid="company-name" className={input} />
                      <input value={company.company_nip} onChange={(e) => setCompany({ ...company, company_nip: e.target.value })} placeholder={t("NIP")} data-testid="company-nip" className={input} />
                      <input value={company.company_rpt} onChange={(e) => setCompany({ ...company, company_rpt: e.target.value })} placeholder={t("Numer pozwolenia (RPT — sprawozdania UKE)")} data-testid="company-rpt" className={input} />
                      <input value={company.company_phone} onChange={(e) => setCompany({ ...company, company_phone: e.target.value })} placeholder={t("Telefon")} data-testid="company-phone" className={input} />
                      <input value={company.company_email} onChange={(e) => setCompany({ ...company, company_email: e.target.value })} placeholder={t("E-mail")} data-testid="company-email" className={input} />
                      <input value={company.company_website} onChange={(e) => setCompany({ ...company, company_website: e.target.value })} placeholder={t("Strona WWW operatora (dla App klienta)")} data-testid="company-website" className={input} />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">{t("Dane trafią automatycznie na stronę tytułową i do stopki pobieranego opisu programu (PDF).")}</p>
                    <div className="mt-3 border-t border-slate-700 pt-3">
                      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Logo firmy (nagłówek protokołu PDF)")}</label>
                      <div className="flex items-center gap-3">
                        {company.company_logo_file_id
                          ? <img src={`${API}/files/${company.company_logo_file_id}?auth=${encodeURIComponent(dlToken())}`} alt="logo" className="h-12 bg-white rounded p-1 border border-slate-700" data-testid="company-logo-preview" />
                          : <span className="text-xs text-slate-500">{t("Brak logo")}</span>}
                        <label className="bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm cursor-pointer flex items-center gap-2" data-testid="company-logo-upload">
                          {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} {t("Wgraj logo")}
                          <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
                        </label>
                        {company.company_logo_file_id && (
                          <button type="button" onClick={() => setCompany((c) => ({ ...c, company_logo_file_id: "" }))} className="text-xs text-red-400 underline" data-testid="company-logo-remove">{t("Usuń")}</button>
                        )}
                      </div>
                    </div>
                  </Section>

                  {/* Zdjęcia pracowników */}
                  <Section icon={Users} iconColor="text-pink-400" title={t("Zdjęcia pracowników")}
                    open={open === "empphotos"} onToggle={() => toggle("empphotos")} testid="settings-sec-empphotos">
                    <div className="mb-4 pb-3 border-b border-slate-700">
                      <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Domyślna ważność identyfikatora (miesiące)")}</label>
                      <input type="number" min="1" max="120" value={badgeMonths} onChange={(e) => setBadgeMonths(e.target.value)} data-testid="badge-validity-months" className={input} />
                      <p className="text-[11px] text-slate-500 mt-1">{t("Używana, gdy przy pracowniku nie ustawisz własnej wartości. Zapisz przyciskiem na dole.")}</p>
                    </div>
                    <EmployeePhotos t={t} />
                  </Section>

                  {/* Narzędzia i nawigacja */}
                  <Section icon={Library} title={t("Narzędzia i nawigacja")}
                    open={open === "nav"} onToggle={() => toggle("nav")} testid="settings-nav">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <button onClick={() => nav("logs")} data-testid="nav-logs" className={navBtn}><History size={15} className="text-cyan-400" /> {t("Historia")}</button>
                      <button onClick={() => nav("reports")} data-testid="nav-reports" className={navBtn}><BarChart3 size={15} className="text-cyan-400" /> {t("Raporty")}</button>
                      <button onClick={() => nav("documents")} data-testid="nav-documents" className={navBtn}><FileText size={15} className="text-cyan-400" /> {t("Dokumentacja")}</button>
                      <button onClick={() => nav("cable_catalog")} data-testid="nav-cat-cable" className={navBtn}><Library size={15} className="text-green-400" /> {t("Katalog kabli")}</button>
                      <button onClick={() => nav("closure_catalog")} data-testid="nav-cat-closure" className={navBtn}><Library size={15} className="text-green-400" /> {t("Katalog muf")}</button>
                      <button onClick={() => nav("odf_catalog")} data-testid="nav-cat-odf" className={navBtn}><Library size={15} className="text-green-400" /> {t("Katalog szaf/ODF")}</button>
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/program-pdf?auth=${encodeURIComponent(dlToken())}`}
                      target="_blank" rel="noreferrer" data-testid="nav-program-pdf"
                      className="mt-2 flex items-center justify-center gap-2 rounded-md border border-cyan-700 bg-cyan-900/30 px-3 py-2.5 text-sm text-cyan-200 hover:bg-cyan-900/50 transition-colors">
                      <FileText size={15} className="text-cyan-300" /> {t("Pobierz opis programu (PDF)")}
                    </a>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/program-pdf?lang=en&auth=${encodeURIComponent(dlToken())}`}
                        target="_blank" rel="noreferrer" data-testid="nav-program-pdf-en"
                        className="flex items-center justify-center gap-2 rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700/60 transition-colors">
                        <FileText size={14} className="text-cyan-300" /> {t("Opis programu — EN (PDF)")}
                      </a>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/program-pdf?lang=de&auth=${encodeURIComponent(dlToken())}`}
                        target="_blank" rel="noreferrer" data-testid="nav-program-pdf-de"
                        className="flex items-center justify-center gap-2 rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700/60 transition-colors">
                        <FileText size={14} className="text-cyan-300" /> {t("Opis programu — DE (PDF)")}
                      </a>
                    </div>
                    <button onClick={dlPackage} disabled={dlBusy} data-testid="nav-download-package"
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-md border border-emerald-700 bg-emerald-900/30 px-3 py-2.5 text-sm text-emerald-200 hover:bg-emerald-900/50 disabled:opacity-50 transition-colors">
                      {dlBusy ? <Loader2 size={15} className="animate-spin" /> : <DownloadCloud size={15} className="text-emerald-300" />}
                      {dlBusy ? t("Pobieram…") : t("Pobierz paczkę aktualizacyjną (ZIP)")}
                    </button>
                    <p className="text-[11px] text-slate-500 mt-1.5">{t("Pobiera pełny pakiet instalacyjny/aktualizacyjny (backend + frontend + skrypty). Wgraj go na serwer i uruchom ./instaluj.sh (nowa instalacja) lub ./aktualizuj.sh (aktualizacja).")}</p>
                    <a href="/prezentacja" target="_blank" rel="noreferrer" data-testid="nav-presentation"
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-md border border-cyan-700 bg-cyan-900/30 px-3 py-2.5 text-sm text-cyan-200 hover:bg-cyan-900/50 transition-colors">
                      <FileText size={15} className="text-cyan-300" /> {t("Prezentacja programu (film 3 min do nagrania)")}
                    </a>
                    <p className="text-[11px] text-slate-500 mt-1.5">{t("Otwiera auto-prezentację (mapa, spawy, PDF, monter, kamery, SNMP) z napisami i lektorem. Włącz pełny ekran i nagraj ekran w 1920×1080. Napisy .srt: /prezentacja/napisy.srt")}</p>
                    <button onClick={dlFilm} disabled={filmBusy} data-testid="nav-download-film"
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-md border border-purple-700 bg-purple-900/30 px-3 py-2.5 text-sm text-purple-200 hover:bg-purple-900/50 disabled:opacity-50 transition-colors">
                      {filmBusy ? <Loader2 size={15} className="animate-spin" /> : <DownloadCloud size={15} className="text-purple-300" />}
                      {filmBusy ? t("Pobieram…") : t("Pobierz gotowy film MP4 (FHD ~5 min)")}
                    </button>
                    <p className="text-[11px] text-slate-500 mt-1.5">{t("Gotowy plik wideo 1920×1080 z lektorem AI i napisami — do wysłania klientom lub publikacji. Nie trzeba nic nagrywać.")}</p>
                    <a href="/prezentacja/audio/narracja-lektor.mp3" download data-testid="nav-narration-mp3"
                      className="mt-2 w-full flex items-center justify-center gap-2 rounded-md border border-emerald-700 bg-emerald-900/30 px-3 py-2.5 text-sm text-emerald-200 hover:bg-emerald-900/50 transition-colors">
                      <DownloadCloud size={15} className="text-emerald-300" /> {t("Pobierz narrację lektora AI (MP3)")}
                    </a>
                  </Section>

                  {/* Plany prędkości + App klienta */}
                  <Section icon={Radar} iconColor="text-cyan-400" title={t("Plany prędkości i App klienta")}
                    open={open === "portal"} onToggle={() => toggle("portal")} testid="settings-sec-portal">
                    <ClientAppCard t={t} portalUrl={portalUrl} embedded />
                    <div className="border-t border-slate-700 my-4" />
                    <NotificationsPanel t={t} />
                    <div className="border-t border-slate-700 my-4" />
                    <label className="text-xs font-semibold text-slate-300 mb-1.5 block" data-testid="sms-templates-editor">{t("Szablony szybkich odpowiedzi SMS")}</label>
                    <p className="text-[11px] text-slate-500 mb-2">{t("Gotowe teksty do wstawienia jednym kliknięciem przy odpowiedzi w Skrzynce SMS.")}</p>
                    <div className="space-y-1.5 mb-2">
                      {smsTpls.map((tpl, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="flex-1">
                            <input value={tpl} data-testid={`sms-tpl-input-${i}`} onChange={(e) => setSmsTpls((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
                              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                            <div className={`text-[10px] mt-0.5 ${tpl.length > 160 ? "text-amber-400" : "text-slate-500"}`} data-testid={`sms-tpl-count-${i}`}>{tpl.length}/160{tpl.length > 160 ? ` · ${Math.ceil(tpl.length / 153)} SMS` : ""}</div>
                          </div>
                          <button onClick={() => setSmsTpls((a) => a.filter((_, j) => j !== i))} data-testid={`sms-tpl-del-${i}`} className="text-slate-500 hover:text-red-400 p-1 mt-1.5"><Trash2 size={15} /></button>
                        </div>
                      ))}
                      {smsTpls.length === 0 && <p className="text-[11px] text-slate-600">{t("Brak szablonów — dodaj pierwszy.")}</p>}
                    </div>
                    <div className="flex gap-2 mb-1">
                      <button onClick={() => setSmsTpls((a) => [...a, ""])} data-testid="sms-tpl-add" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1.5"><Plus size={14} /> {t("Dodaj szablon")}</button>
                      <button onClick={saveSmsTpls} data-testid="sms-tpl-save" className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white rounded px-3 py-1.5 flex items-center gap-1.5"><Save size={14} /> {t("Zapisz szablony")}</button>
                    </div>
                    <div className="border-t border-slate-700 my-4" />
                    <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Plany prędkości (do wyboru u klienta)")}</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {speedPlans.map((p) => (
                        <span key={p} data-testid={`plan-chip-${p}`} className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-full pl-3 pr-1.5 py-1 text-xs">
                          {p}
                          <button onClick={() => setSpeedPlans(speedPlans.filter((x) => x !== p))} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>
                        </span>
                      ))}
                      {speedPlans.length === 0 && <span className="text-xs text-slate-500">{t("Brak planów")}</span>}
                    </div>
                    <div className="flex gap-2 mb-4">
                      <input value={newPlan} onChange={(e) => setNewPlan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newPlan.trim()) { setSpeedPlans([...speedPlans, newPlan.trim()]); setNewPlan(""); } }}
                        placeholder="np. Standard 300/50" data-testid="plan-input" className={input} />
                      <button onClick={() => { if (newPlan.trim()) { setSpeedPlans([...speedPlans, newPlan.trim()]); setNewPlan(""); } }} data-testid="plan-add" className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm"><Plus size={15} /> {t("Dodaj")}</button>
                    </div>
                    <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Adres aplikacji klienta (do kodu QR na umowie)")}</label>
                    <input value={portalUrl} onChange={(e) => setPortalUrl(e.target.value)} placeholder="https://twoja-domena.pl/klient" data-testid="portal-url" className={`${input} mb-3`} />
                    <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Instrukcja konfiguracji routera/WiFi (App klienta)")}</label>
                    <textarea value={portalCfg.router_instructions} onChange={(e) => setPortalCfg({ ...portalCfg, router_instructions: e.target.value })} data-testid="router-instructions" rows={5} className={`${input} font-mono text-xs`} />
                    <label className="text-xs font-semibold text-slate-300 mb-1 mt-4 block">{t("Szablon umowy (PDF) — znaczniki: {name} {address} {plan} {download} {upload} {ip} {bank_account} {company_name} {date} {contract_no}")}</label>
                    <textarea value={portalCfg.contract_template} onChange={(e) => setPortalCfg({ ...portalCfg, contract_template: e.target.value })} data-testid="contract-template" rows={6} className={`${input} font-mono text-xs`} />
                    <label className="text-xs font-semibold text-slate-300 mb-1 mt-4 block">{t("Szablon zgody RODO (PDF)")}</label>
                    <textarea value={portalCfg.rodo_template} onChange={(e) => setPortalCfg({ ...portalCfg, rodo_template: e.target.value })} data-testid="rodo-template" rows={5} className={`${input} font-mono text-xs`} />
                    <p className="text-[11px] text-slate-500 mt-2">{t("App klienta: /klient — logowanie e-mailem i kodem PIN (PIN widoczny w karcie klienta).")}</p>
                  </Section>

                  {/* O programie / Licencje */}
                  <Section icon={Library} iconColor="text-indigo-400" title={t("O programie / Licencje")}
                    open={open === "about"} onToggle={() => toggle("about")} testid="settings-sec-about">
                    <p className="text-[11px] text-slate-400 mb-3" data-testid="about-intro">
                      {t("Produkt można sprzedawać jako oprogramowanie zamknięte. Wszystkie użyte komponenty mają licencje permisywne (MIT/BSD/Apache/ISC) lub słabe copyleft plikowe (LGPL/MPL) używane jako biblioteki — bez obowiązku ujawniania kodu źródłowego produktu. Czcionki na SIL OFL 1.1.")}
                    </p>
                    <div className="space-y-3">
                      {LICENSE_GROUPS.map((g) => (
                        <div key={g.title} className="bg-slate-900/60 border border-slate-700 rounded p-3" data-testid={`about-group-${g.title}`}>
                          <div className="text-xs font-semibold text-cyan-400 mb-1.5">{g.title}</div>
                          <ul className="list-disc list-inside space-y-1">
                            {g.items.map((it, i) => <li key={i} className="text-[11px] text-slate-300 leading-relaxed">{it}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-3">
                      {t("Pełny wykaz licencji znajduje się w pliku THIRD-PARTY-LICENSES.md dołączonym do dystrybucji.")}
                    </p>
                    <button type="button"
                      onClick={() => window.open(`${API}/about/licenses.pdf?auth=${encodeURIComponent(dlToken())}`, "_blank")}
                      data-testid="about-download-pdf"
                      className="mt-3 inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm">
                      <FileText size={14} /> {t("Pobierz wykaz licencji (PDF)")}
                    </button>
                  </Section>
                </>
              )}
            </div>
          )}

          {/* ============ CENNIK I FAKTURY ============ */}
          {activeTab === "cennik" && isAdmin && (
            <div className="space-y-3">
              {/* Ceny i faktury */}
              <Section icon={FileText} iconColor="text-cyan-400" title={t("Ceny i faktury")}
                open={open === "prices"} onToggle={() => toggle("prices")} testid="settings-sec-prices">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div><label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Stawka VAT [%]")}</label><input type="number" value={vatRate} onChange={(e) => setVatRate(e.target.value)} data-testid="vat-rate" className={input} /></div>
                  <div><label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Prefiks numeru faktury")}</label><input value={invoicePrefix} onChange={(e) => setInvoicePrefix(e.target.value)} data-testid="invoice-prefix" className={input} /></div>
                </div>
                {[["Abonament internet (plany prędkości)", speedPlans, planPrices, setPlanPrices],
                  ["IPTV", iptvPlans, iptvPrices, setIptvPrices],
                  ["VoIP", voipTariffs, voipPrices, setVoipPrices],
                  ["Telefonia komórkowa", mobilePlans, mobilePrices, setMobilePrices]].map(([lbl, list, prices, setP]) => (
                  <div className="mb-3" key={lbl}>
                    <div className="text-[11px] uppercase text-slate-500 mb-1">{t(lbl)}</div>
                    {(list && list.length > 0) ? list.map((p) => (
                      <div key={p} className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-300 flex-1 truncate">{p}</span>
                        <input type="number" step="0.01" value={prices[p] ?? ""} onChange={(e) => setP({ ...prices, [p]: e.target.value })} placeholder={t("zł netto")} data-testid={`price-${p}`} className={`${input} w-28`} />
                      </div>
                    )) : <div className="text-[11px] text-slate-600">{t("Brak pozycji - dodaj plany w sekcjach Plany predkosci / Pakiety.")}</div>}
                  </div>
                ))}
                <label className="flex items-center gap-2 mt-1 text-sm text-slate-200 cursor-pointer" data-testid="auto-invoice-toggle">
                  <input type="checkbox" checked={!!autoInvoice} onChange={(e) => setAutoInvoice(e.target.checked)} className="accent-cyan-500" />
                  {t("Automatycznie wystawiaj faktury co miesiąc (1. dnia) + powiadom klienta (push/e-mail)")}
                </label>
                <p className="text-[11px] text-slate-500 mt-2">{t("Faktura tworzy sie automatycznie przy dodaniu klienta (uslugi x ceny). Pelna lista w menu Faktury.")}</p>
              </Section>

              {/* Pakiety IPTV / VoIP / Telefonia */}
              <Section icon={FileText} iconColor="text-cyan-400" title={t("Pakiety: IPTV / VoIP / Telefonia komórkowa")}
                open={open === "svcplans"} onToggle={() => toggle("svcplans")} testid="settings-sec-svcplans">
                <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Pakiety IPTV")}</label>
                <div className="flex gap-2 mb-2">
                  <input value={newSvc.iptv} onChange={(e) => setNewSvc({ ...newSvc, iptv: e.target.value })} placeholder="np. IPTV Start" data-testid="iptv-input" className={input} />
                  <button onClick={() => { const v = newSvc.iptv.trim(); if (v && !iptvPlans.includes(v)) { setIptvPlans([...iptvPlans, v]); setNewSvc({ ...newSvc, iptv: "" }); } }} data-testid="iptv-add" className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm"><Plus size={15} /></button>
                </div>
                {iptvPlans.map((p) => (
                  <div key={p} className="rounded border border-slate-700 bg-slate-900/50 p-2 mb-2" data-testid={`iptv-plan-${p}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-slate-200">{p}</span>
                      <button onClick={() => setIptvPlans(iptvPlans.filter((x) => x !== p))} className="text-slate-500 hover:text-red-400"><Trash2 size={13} /></button>
                    </div>
                    <textarea value={(iptvChannels[p] || []).join("\n")} onChange={(e) => setIptvChannels({ ...iptvChannels, [p]: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} placeholder={t("Kanały — jeden na linię")} rows={3} data-testid={`iptv-channels-${p}`} className={`${input} font-mono text-xs`} />
                    <div className="flex items-center gap-2 mt-1">
                      <label className="text-[11px] text-slate-400">{t("lub własny PDF:")}</label>
                      <input type="file" accept="application/pdf" data-testid={`iptv-pdf-${p}`} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("entity_type", "iptv"); fd.append("entity_id", p); fd.append("file", f); try { const { data } = await api.post("/upload", fd); if (data.id) { setIptvPdf({ ...iptvPdf, [p]: `${API}/files/${data.id}` }); toast.success(t("Wgrano PDF")); } } catch { toast.error(t("Błąd wgrywania")); } }} className="text-[11px]" />
                      {iptvPdf[p] && <span className="text-[11px] text-emerald-400">✓ PDF</span>}
                    </div>
                  </div>
                ))}
                <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-3 block">{t("Taryfy VoIP")}</label>
                <div className="flex gap-2 mb-2">
                  <input value={newSvc.voip} onChange={(e) => setNewSvc({ ...newSvc, voip: e.target.value })} placeholder="np. VoIP Domowy" data-testid="voip-input" className={input} />
                  <button onClick={() => { const v = newSvc.voip.trim(); if (v && !voipTariffs.includes(v)) { setVoipTariffs([...voipTariffs, v]); setNewSvc({ ...newSvc, voip: "" }); } }} data-testid="voip-add" className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm"><Plus size={15} /></button>
                </div>
                {voipTariffs.map((p) => (
                  <div key={p} className="rounded border border-slate-700 bg-slate-900/50 p-2 mb-2" data-testid={`voip-tariff-${p}`}>
                    <div className="flex items-center justify-between mb-1"><span className="text-sm font-semibold text-slate-200">{p}</span><button onClick={() => setVoipTariffs(voipTariffs.filter((x) => x !== p))} className="text-slate-500 hover:text-red-400"><Trash2 size={13} /></button></div>
                    <textarea value={voipDetails[p] || ""} onChange={(e) => setVoipDetails({ ...voipDetails, [p]: e.target.value })} placeholder={t("Stawki / opis taryfy")} rows={2} className={`${input} text-xs`} />
                  </div>
                ))}
                <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-3 block">{t("Plany telefonii komórkowej")}</label>
                <div className="flex gap-2 mb-2">
                  <input value={newSvc.mobile} onChange={(e) => setNewSvc({ ...newSvc, mobile: e.target.value })} placeholder={t("np. Komórka 30GB")} data-testid="mobile-input" className={input} />
                  <button onClick={() => { const v = newSvc.mobile.trim(); if (v && !mobilePlans.includes(v)) { setMobilePlans([...mobilePlans, v]); setNewSvc({ ...newSvc, mobile: "" }); } }} data-testid="mobile-add" className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm"><Plus size={15} /></button>
                </div>
                {mobilePlans.map((p) => (
                  <div key={p} className="rounded border border-slate-700 bg-slate-900/50 p-2 mb-2" data-testid={`mobile-plan-${p}`}>
                    <div className="flex items-center justify-between mb-1"><span className="text-sm font-semibold text-slate-200">{p}</span><button onClick={() => setMobilePlans(mobilePlans.filter((x) => x !== p))} className="text-slate-500 hover:text-red-400"><Trash2 size={13} /></button></div>
                    <textarea value={mobileDetails[p] || ""} onChange={(e) => setMobileDetails({ ...mobileDetails, [p]: e.target.value })} placeholder={t("Opis planu")} rows={2} className={`${input} text-xs`} />
                  </div>
                ))}
                <p className="text-[11px] text-slate-500 mt-2">{t("Pakiety pojawią się do wyboru w karcie klienta oraz w aplikacji klienta.")}</p>
              </Section>

              {/* Testy prędkości klientów */}
              <Section icon={FileText} iconColor="text-cyan-400" title={t("Testy prędkości klientów")}
                open={open === "clienttests"} onToggle={() => toggle("clienttests")} testid="settings-sec-clienttests">
                <button onClick={async () => { try { const { data } = await api.get("/speedtests/all"); setStests(data || []); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid="stests-load" className="bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 text-sm mb-3">{t("Załaduj wyniki")}</button>
                {stests && (stests.length === 0 ? <div className="text-xs text-slate-500">{t("Brak pomiarów.")}</div> : (
                  <div className="rounded border border-slate-700 divide-y divide-slate-800 max-h-72 overflow-y-auto">
                    {stests.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`stest-row-${i}`}>
                        <span className="text-slate-200">{r.client_name}</span>
                        <span className="font-mono text-slate-400">{r.download_mbps}/{r.upload_mbps} Mb/s · {r.ping_ms} ms</span>
                        <span className="text-slate-500">{(r.at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </Section>
            </div>
          )}

          {/* ============ SIEĆ ============ */}
          {activeTab === "siec" && isAdmin && (
            <div className="space-y-3">
              {/* Technologie */}
              <Section icon={Cable} title={t("Technologie podłączenia klientów")}
                open={open === "tech"} onToggle={() => toggle("tech")} testid="settings-sec-tech">
                <div className="flex flex-wrap gap-2 mb-3">
                  {techs.map((t) => (
                    <span key={t} data-testid={`tech-chip-${t}`} className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-full pl-3 pr-1.5 py-1 text-xs">
                      {t}
                      <button onClick={() => removeTech(t)} data-testid={`tech-remove-${t}`} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>
                    </span>
                  ))}
                  {techs.length === 0 && <span className="text-xs text-slate-500">{t("Brak technologii")}</span>}
                </div>
                <div className="flex gap-2">
                  <input value={newTech} onChange={(e) => setNewTech(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTech()}
                    placeholder="np. XGS-PON" data-testid="tech-input" className={input} />
                  <button onClick={addTech} data-testid="tech-add" className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded px-3 flex items-center gap-1 text-sm"><Plus size={15} /> {t("Dodaj")}</button>
                </div>
              </Section>

              {/* Zasięgi — słowniki */}
              <Section icon={Radar} title={t("Zasięgi sieci — słowniki")}
                open={open === "coverage"} onToggle={() => toggle("coverage")} testid="settings-sec-coverage">
                <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Medium transmisyjne")}</label>
                {renderChips(covMedia, setCovMedia, newCov.media, "media", "np. światłowodowe", "cov-media")}
                <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-4 block">{t("Technologia dostępowa")}</label>
                {renderChips(covTechs, setCovTechs, newCov.tech, "tech", "np. FTTH", "cov-tech")}
                <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-4 block">{t("Przepustowości [Mb/s]")}</label>
                {renderChips(covBw, setCovBw, newCov.bw, "bw", "np. 1000", "cov-bw")}
                <p className="text-[11px] text-slate-500 mt-2">{t("Wartości używane w formularzu „Zasięgi Sieci\" (medium, technologia, maks. przepustowość DL/UL).")}</p>
              </Section>

              {/* Standardy kolorów włókien */}
              <Section icon={FileText} iconColor="text-cyan-400" title={t("Standardy kolorów włókien")}
                open={open === "fiberstd"} onToggle={() => toggle("fiberstd")} testid="settings-sec-fiberstd">
                <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Domyślny standard (dla nowych kabli)")}</label>
                <select value={defStd} onChange={(e) => setDefStd(e.target.value)} data-testid="fiberstd-default" className={`${input} mb-3`}>
                  {fiberStds.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
                <div className="space-y-2 mb-3">
                  {fiberStds.map((s, idx) => (
                    <div key={s.name} className="rounded border border-slate-700 bg-slate-900/50 p-2" data-testid={`fiberstd-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-200">{s.name}</span>
                        <button onClick={() => setFiberStds(fiberStds.filter((x) => x.name !== s.name))} className="text-slate-500 hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">{(s.colors || []).join(", ")}</div>
                    </div>
                  ))}
                </div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Dodaj własny standard")}</label>
                <input value={newStd.name} onChange={(e) => setNewStd({ ...newStd, name: e.target.value })} placeholder={t("Nazwa standardu")} data-testid="fiberstd-name" className={`${input} mb-1`} />
                <input value={newStd.colors} onChange={(e) => setNewStd({ ...newStd, colors: e.target.value })} placeholder={t("Kolory po przecinku (od włókna 1)")} data-testid="fiberstd-colors" className={`${input} mb-1`} />
                <button onClick={() => { const nm = newStd.name.trim(); const cols = newStd.colors.split(",").map((x) => x.trim()).filter(Boolean); if (nm && cols.length && !fiberStds.some((x) => x.name === nm)) { setFiberStds([...fiberStds, { name: nm, colors: cols }]); setNewStd({ name: "", colors: "" }); } }} data-testid="fiberstd-add" className="bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 text-sm flex items-center gap-1"><Plus size={15} /> {t("Dodaj")}</button>
              </Section>

              {/* Budżet optyczny */}
              <Section icon={Activity} title={t("Budżet optyczny")}
                open={open === "budget"} onToggle={() => toggle("budget")} testid="settings-sec-budget">
                <div className="mb-3 space-y-2 border-b border-slate-800 pb-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{t("Ułatwienia dodawania")}</div>
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input type="checkbox" data-testid="remember-last-closure" checked={!!settings.remember_last_closure}
                      onChange={(e) => save({ remember_last_closure: e.target.checked })} className="accent-cyan-500" />
                    {t("Zapamiętuj dane ostatnio dodanej mufy (wstępnie wypełnij nowy formularz)")}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input type="checkbox" data-testid="remember-last-slack" checked={!!settings.remember_last_slack}
                      onChange={(e) => save({ remember_last_slack: e.target.checked })} className="accent-cyan-500" />
                    {t("Zapamiętuj ostatni zapas kabla (długość i opis)")}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="text-slate-400">{t("Maks. tłumienie OTDR [dB] — próg zielony/czerwony")}</span>
                    <input type="number" step="0.1" min="0" data-testid="otdr-max-loss" value={settings.otdr_max_loss_db ?? 5}
                      onChange={(e) => save({ otdr_max_loss_db: parseFloat(e.target.value) || 0 })}
                      className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t("Moc nadawcza OLT [dBm]")}</label>
                    <input type="number" step="any" value={budget.olt_tx_power_dbm} data-testid="budget-tx"
                      onChange={(e) => setBudget({ ...budget, olt_tx_power_dbm: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t("Min. moc odbioru ONT [dBm]")}</label>
                    <input type="number" step="any" value={budget.min_rx_dbm} data-testid="budget-minrx"
                      onChange={(e) => setBudget({ ...budget, min_rx_dbm: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t("Tolerancja odchyłki [dB]")}</label>
                    <input type="number" step="any" value={budget.budget_tolerance_db} data-testid="budget-tol"
                      onChange={(e) => setBudget({ ...budget, budget_tolerance_db: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t("Długość fali")}</label>
                    <select value={budget.budget_wavelength} data-testid="budget-wavelength"
                      onChange={(e) => setBudget({ ...budget, budget_wavelength: e.target.value })} className={input}>
                      <option value="1310">1310 nm</option>
                      <option value="1550">1550 nm</option>
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Na tej podstawie system wylicza oczekiwaną moc u klienta i porównuje ją z realnym pomiarem, oznaczając klientów przekraczających próg.</p>

                <div className="mt-4 pt-3 border-t border-slate-700">
                  <label className="text-xs font-semibold text-slate-300 mb-2 block">Tłumienie splitterów [dB] wg podziału 1:XX</label>
                  <div className="grid grid-cols-3 gap-2">
                    {SPLIT_TYPES.map((t) => (
                      <div key={t}>
                        <label className="text-[11px] text-slate-400 mb-1 block">{t}</label>
                        <input type="number" step="any" value={splitterLoss[t]} data-testid={`loss-${t.replace(":", "-")}`}
                          onChange={(e) => setSplitterLoss({ ...splitterLoss, [t]: e.target.value })} className={input} />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">{t("Wartości używane w bilansie mocy i w ostrzeżeniach kaskady splitterów (np. 1:4 → 1:64 = 1:256).")}</p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-700" data-testid="settings-autoplan-prices">
                  <label className="text-xs font-semibold text-slate-300 mb-2 block">Cennik Auto-projektanta (szacowany koszt wariantów)</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 mb-1 block">Kabel [zł/m]</label>
                      <input type="number" step="any" value={budget.autoplan_price_cable_m} data-testid="ap-price-cable"
                        onChange={(e) => setBudget({ ...budget, autoplan_price_cable_m: e.target.value })} className={input} />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 mb-1 block">Mufa [zł/szt.]</label>
                      <input type="number" step="any" value={budget.autoplan_price_closure} data-testid="ap-price-closure"
                        onChange={(e) => setBudget({ ...budget, autoplan_price_closure: e.target.value })} className={input} />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 mb-1 block">Splitter [zł/szt.]</label>
                      <input type="number" step="any" value={budget.autoplan_price_splitter} data-testid="ap-price-splitter"
                        onChange={(e) => setBudget({ ...budget, autoplan_price_splitter: e.target.value })} className={input} />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">Stawki użyte do kolumny „Szac. koszt" w porównaniu wariantów Auto-projektanta.</p>
                </div>
              </Section>

              {/* Baza adresowa */}
              <Section icon={MapPin} title={t("Baza adresowa")}
                open={open === "addr"} onToggle={() => toggle("addr")} testid="settings-sec-addr">
                <p className="text-[11px] text-slate-500 mb-3">{t("Wgraj plik CSV z punktami adresowymi — osobno dla każdego województwa (duże pliki). Rozpoznawane kolumny: szerokość/długość (lub lat/lng), TERYT, SIMC, ulica, miejscowość, numer. Ponowny import dla tego samego województwa nadpisuje jego dane.")}</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t("Województwo *")}</label>
                    <select value={addrWoj} onChange={(e) => setAddrWoj(e.target.value)} data-testid="addr-woj" className={input}>
                      <option value="">{t("— wybierz —")}</option>
                      {WOJEWODZTWA.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t("Układ (EPSG)")}</label>
                    <input value={addrEpsg} onChange={(e) => setAddrEpsg(e.target.value)} placeholder={t("puste = WGS84")}
                      data-testid="addr-epsg" className={input} />
                  </div>
                </div>
                <input type="file" accept=".csv,text/csv" data-testid="addr-file"
                  onChange={(e) => setAddrFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-700 file:text-slate-100 file:cursor-pointer mb-2" />
                <button onClick={importAddresses} disabled={addrBusy} data-testid="addr-import"
                  className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60 mb-3">
                  {addrBusy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {t("Wgraj województwo")}
                </button>

                <div className="text-xs text-slate-400 mb-1 flex items-center justify-between">
                  <span>{t("Załadowane województwa")}</span>
                  <span className="font-mono text-slate-300" data-testid="addr-total">{t("razem:")} {addrTotal}</span>
                </div>
                <div className="space-y-1" data-testid="addr-provinces">
                  {provinces.length === 0 && <div className="text-xs text-slate-500 px-1 py-2">{t("Brak wczytanych danych adresowych.")}</div>}
                  {provinces.map((p) => (
                    <div key={p.wojewodztwo} data-testid={`addr-prov-${p.wojewodztwo}`}
                      className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-sm">
                      <MapPin size={13} className="text-cyan-400 shrink-0" />
                      <span className="truncate">{p.wojewodztwo}</span>
                      <span className="ml-auto text-xs text-slate-500 font-mono">{p.count}</span>
                      <button onClick={() => deleteProvince(p.wojewodztwo)} data-testid={`addr-del-${p.wojewodztwo}`}
                        title={t("Usuń to województwo")} className="text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded p-1 shrink-0"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Konto SIP / VoIP */}
              <Section icon={MapPin} iconColor="text-green-400" title={t("Telefon VoIP (konto SIP)")}
                open={open === "sip"} onToggle={() => toggle("sip")} testid="settings-sec-sip">
                <SipSettings settings={settings} save={save} />
              </Section>

              {/* Powiadomienia */}
              <Section icon={Activity} iconColor="text-red-400" title={t("Powiadomienia o awariach")}
                open={open === "notif"} onToggle={() => toggle("notif")} testid="settings-sec-notif">
                <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                  <input type="checkbox" checked={notif.notify_email_enabled} data-testid="notif-email-toggle"
                    onChange={(e) => setNotif({ ...notif, notify_email_enabled: e.target.checked })} className="accent-cyan-500" />
                  {t("Włącz powiadomienia e-mail (SMTP)")}
                </label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <input value={notif.smtp_host} onChange={(e) => setNotif({ ...notif, smtp_host: e.target.value })} placeholder={t("Serwer SMTP (host)")} data-testid="smtp-host" className={input} />
                  <input value={notif.smtp_port} onChange={(e) => setNotif({ ...notif, smtp_port: e.target.value })} placeholder={t("Port")} data-testid="smtp-port" className={input} />
                  <input value={notif.smtp_user} onChange={(e) => setNotif({ ...notif, smtp_user: e.target.value })} placeholder={t("Użytkownik SMTP")} data-testid="smtp-user" className={input} autoComplete="off" />
                  <input type="password" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} placeholder={settings.smtp_pass_set ? t("Hasło (ustawione — puste = bez zmian)") : t("Hasło SMTP")} data-testid="smtp-pass" className={input} autoComplete="off" />
                  <input value={notif.notify_from_email} onChange={(e) => setNotif({ ...notif, notify_from_email: e.target.value })} placeholder={t("Adres nadawcy")} data-testid="smtp-from" className={input} />
                  <input value={notif.notify_to_emails} onChange={(e) => setNotif({ ...notif, notify_to_emails: e.target.value })} placeholder={t("Odbiorcy (przecinki)")} data-testid="smtp-to" className={input} />
                </div>
                <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                  <input type="checkbox" checked={notif.notify_sms_enabled} data-testid="notif-sms-toggle"
                    onChange={(e) => setNotif({ ...notif, notify_sms_enabled: e.target.checked })} className="accent-cyan-500" />
                  {t("Włącz powiadomienia SMS")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Dostawca SMS")}</label>
                    <select value={notif.sms_provider} onChange={(e) => setNotif({ ...notif, sms_provider: e.target.value })} data-testid="sms-provider" className={input}>
                      {SMS_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  {notif.sms_provider === "twilio" && (
                    <input value={notif.sms_account_sid} onChange={(e) => setNotif({ ...notif, sms_account_sid: e.target.value })} placeholder={t("Twilio Account SID")} data-testid="sms-sid" className={`${input} col-span-2`} autoComplete="off" />
                  )}
                  {isHwSms && (
                    <>
                      <input value={notif.sms_host} onChange={(e) => setNotif({ ...notif, sms_host: e.target.value })} placeholder={t("Adres bramki (IP lub host, np. 192.168.1.10)")} data-testid="sms-host" className={`${input} col-span-2`} autoComplete="off" />
                      <input value={notif.sms_port} onChange={(e) => setNotif({ ...notif, sms_port: e.target.value })} placeholder={t("Port (opcjonalnie)")} data-testid="sms-port" className={input} autoComplete="off" />
                      <input value={notif.sms_username} onChange={(e) => setNotif({ ...notif, sms_username: e.target.value })} placeholder={t("Użytkownik / login")} data-testid="sms-username" className={input} autoComplete="off" />
                      <input value={notif.sms_line} onChange={(e) => setNotif({ ...notif, sms_line: e.target.value })} placeholder={smsLinePlaceholder} data-testid="sms-line" className={`${input} col-span-2`} autoComplete="off" />
                      <label className="flex items-center gap-2 text-xs text-slate-300 col-span-2 cursor-pointer">
                        <input type="checkbox" checked={notif.sms_use_tls} onChange={(e) => setNotif({ ...notif, sms_use_tls: e.target.checked })} data-testid="sms-use-tls" className="accent-cyan-500" />
                        {t("Połączenie HTTPS (TLS) z bramką")}
                      </label>
                    </>
                  )}
                  {!isHwSms && (
                    <input value={notif.sms_from} onChange={(e) => setNotif({ ...notif, sms_from: e.target.value })} placeholder={t("Nadawca (numer lub nazwa)")} data-testid="sms-from" className={input} />
                  )}
                  <input type="password" value={smsToken} onChange={(e) => setSmsToken(e.target.value)} placeholder={smsTokenPlaceholder()} data-testid="sms-token" className={input} autoComplete="off" />
                  <input value={notif.sms_to} onChange={(e) => setNotif({ ...notif, sms_to: e.target.value })} placeholder={t("Odbiorcy SMS (przecinki, +48…)")} data-testid="sms-to" className={`${input} col-span-2`} />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={testNotif} data-testid="notif-test" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1.5"><Activity size={13} /> {t("Wyślij testowe powiadomienie")}</button>
                  <button onClick={testSmtp} disabled={smtpTestBusy} data-testid="smtp-test" className="text-xs bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-60">{smtpTestBusy ? <Loader2 size={13} className="animate-spin" /> : <Activity size={13} />} {t("Testuj SMTP")}</button>
                  <button onClick={checkSmsBalance} disabled={balBusy} data-testid="sms-balance-check" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-60">
                    {balBusy ? <Loader2 size={13} className="animate-spin" /> : <Activity size={13} />} {t("Sprawdź saldo SMS")}</button>
                  {isHwSms && (
                    <button onClick={sendSmsGwTest} disabled={smsGwBusy} data-testid="sms-gw-test" className="text-xs bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-60">
                      {smsGwBusy ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} {t("Wyślij testowy SMS przez bramkę")}</button>
                  )}
                  <span className="text-[11px] text-slate-500">{t("(najpierw zapisz ustawienia)")}</span>
                </div>
                {smtpTestResult && (
                  <p className={`text-[11px] mt-1.5 ${smtpTestResult.ok ? "text-green-400" : "text-red-400"}`} data-testid="smtp-test-result">
                    {smtpTestResult.ok ? "✓ " : "⚠ "}{smtpTestResult.detail}
                  </p>
                )}
                {smsGwResult && (
                  <p className="text-[11px] mt-1.5" data-testid="sms-gw-result">
                    {smsGwResult.ok
                      ? <span className="text-green-400">{t("Bramka OK — wysłano do")}: {smsGwResult.results?.map((r) => r.to).join(", ")}</span>
                      : <span className="text-red-400">{t("Błąd bramki")}: {(smsGwResult.results || []).filter((r) => !r.ok).map((r) => `${r.to}: ${r.error || "?"}`).join(" · ") || "?"}</span>}
                  </p>
                )}
                {smsBalance && (
                  <p className="text-[11px] mt-1.5" data-testid="sms-balance-result">
                    {smsBalance.ok
                      ? <span className="text-green-400">{t("Saldo")} ({smsBalance.provider}): <b>{smsBalance.balance ?? "?"} {smsBalance.unit || ""}</b></span>
                      : <span className="text-red-400">{t("Błąd salda")}: {smsBalance.detail || "?"}</span>}
                  </p>
                )}
                <p className="text-[11px] text-slate-500 mt-2">{t("Dostawcy chmurowi: Twilio, SMSAPI, JustSend, SMS-Fly. Bramki sprzętowe: SMSEagle (HTTP API / MHD-8100), Teltonika TRB/RUT, Yeastar / DBL TG, DBL GoIP. Dla bramek podaj adres IP/host, użytkownika i hasło. Dane przechowywane są wyłącznie w Twojej bazie.")}</p>

                <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2" data-testid="sms-inbound-box">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={notif.sms_inbound_enabled} data-testid="sms-inbound-toggle"
                      onChange={(e) => setNotif({ ...notif, sms_inbound_enabled: e.target.checked })} className="accent-cyan-500" />
                    {t("Odbiór SMS — klient odpisuje słowem-kluczem, tworzy zgłoszenie")}
                  </label>
                  {notif.sms_inbound_enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={notif.sms_inbound_keyword} onChange={(e) => setNotif({ ...notif, sms_inbound_keyword: e.target.value })} placeholder={t("Słowo-klucz (np. AWARIA)")} data-testid="sms-inbound-keyword" className={input} />
                        <div className="flex gap-1">
                          <input value={notif.sms_inbound_secret} onChange={(e) => setNotif({ ...notif, sms_inbound_secret: e.target.value })} placeholder={t("Sekret URL")} data-testid="sms-inbound-secret" className={input} />
                          <button type="button" onClick={() => setNotif({ ...notif, sms_inbound_secret: Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 8) })} data-testid="sms-inbound-gen" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 whitespace-nowrap text-slate-200">{t("Generuj")}</button>
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {t("Adres webhooka (skonfiguruj w bramce dla przychodzących SMS):")}
                        <code data-testid="sms-inbound-url" className="block mt-1 break-all text-cyan-300 bg-slate-900 rounded px-2 py-1">{`${process.env.REACT_APP_BACKEND_URL}/api/sms/inbound?secret=${notif.sms_inbound_secret || "…"}`}</code>
                        <span className="text-slate-500">{t("Bramka powinna przekazać parametry: from (numer) i text (treść). Zapisz ustawienia, aby aktywować.")}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Telegram */}
                <label className="flex items-center gap-2 text-sm mb-2 mt-4 cursor-pointer">
                  <input type="checkbox" checked={notif.notify_telegram_enabled} data-testid="notif-telegram-toggle"
                    onChange={(e) => setNotif({ ...notif, notify_telegram_enabled: e.target.checked })} className="accent-cyan-500" />
                  {t("Włącz powiadomienia Telegram")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="password" value={tgToken} onChange={(e) => setTgToken(e.target.value)}
                    placeholder={settings.telegram_token_set ? t("Token bota (ustawiony — puste = bez zmian)") : t("Token bota (z @BotFather)")}
                    data-testid="telegram-token" className={input} autoComplete="off" />
                  <input value={notif.telegram_chat_id} onChange={(e) => setNotif({ ...notif, telegram_chat_id: e.target.value })}
                    placeholder={t("Chat ID (przecinki dla wielu)")} data-testid="telegram-chat" className={input} />
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">{t("Utwórz bota u @BotFather, wpisz token i Chat ID (np. z @userinfobot). Grupy: dodaj bota i użyj ujemnego ID grupy.")}</p>

                {/* Prog alarmu obciazenia uplinku */}
                <div className="mt-4 border-t border-slate-700 pt-3">
                  <label className="text-sm text-slate-300 block mb-1">{t("Próg alarmu obciążenia uplinku (%)")}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" max="100" value={notif.uplink_alarm_threshold}
                      onChange={(e) => setNotif({ ...notif, uplink_alarm_threshold: e.target.value })}
                      data-testid="uplink-threshold" className={`${input} w-28`} />
                    <span className="text-[11px] text-slate-500">{t("Alarm gdy wykorzystanie łącza przekroczy tę wartość.")}</span>
                  </div>
                </div>
              </Section>

              {/* Sieć i dostęp — SNMP / VPN */}
              <Section icon={Router} iconColor="text-green-400" title={t("Sieć i dostęp (SNMP / VPN)")}
                open={open === "net"} onToggle={() => toggle("net")} testid="settings-sec-net">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Interwał odpytywania SNMP [s]")}</label>
                    <input type="number" value={net.snmp_poll_interval} onChange={(e) => setNet({ ...net, snmp_poll_interval: e.target.value })} data-testid="snmp-poll-interval" className={input} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Port odbioru trapów (UDP)")}</label>
                    <input type="number" value={net.snmp_trap_port} onChange={(e) => setNet({ ...net, snmp_trap_port: e.target.value })} data-testid="snmp-trap-port" className={input} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Odświeżanie podglądu na żywo [s]")}</label>
                    <input type="number" value={net.snmp_refresh_interval} onChange={(e) => setNet({ ...net, snmp_refresh_interval: e.target.value })} data-testid="snmp-refresh-interval" className={input} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Promień wykrywania obiektów (monter) [m]")}</label>
                    <input type="number" min="5" max="2000" value={net.nearby_radius_m} onChange={(e) => setNet({ ...net, nearby_radius_m: e.target.value })} data-testid="nearby-radius-m" className={input} />
                  </div>
                  <div className="col-span-3">
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Próg zgłoszenia offline (liczba nieudanych odpytań)")}</label>
                    <input type="number" value={net.snmp_offline_threshold} onChange={(e) => setNet({ ...net, snmp_offline_threshold: e.target.value })} data-testid="snmp-offline-threshold" className={input} />
                    <p className="text-[11px] text-slate-500 mt-1">{t("Zgłoszenie awarii powstaje dopiero po tylu kolejnych nieudanych odpytaniach — chroni przed fałszywym alarmem gdy urządzenie zostanie chwilowo wyłączone.")}</p>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-3 mb-3">
                  <label className="text-xs font-semibold text-slate-300 mb-2 block flex items-center gap-1.5"><Router size={13} className="text-green-400" /> {t("VLAN — interfejs wirtualny na karcie sieciowej")}</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" value={net.vlan_id} onChange={(e) => setNet({ ...net, vlan_id: e.target.value })} placeholder={t("VLAN ID (globalny)")} data-testid="vlan-id" className={input} />
                    <input value={net.vlan_parent} onChange={(e) => setNet({ ...net, vlan_parent: e.target.value })} placeholder={t("Interfejs sieciowy (NIC)")} data-testid="vlan-parent" className={input} />
                    <button onClick={applyVlan} disabled={vlanBusy} data-testid="vlan-apply" className="bg-green-700 hover:bg-green-600 disabled:opacity-60 rounded px-3 py-2 text-sm flex items-center justify-center gap-1.5">
                      {vlanBusy ? <Loader2 size={14} className="animate-spin" /> : <Router size={14} />} {t("Zastosuj VLAN na serwerze")}</button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{t("Tworzenie VLAN wymaga uprawnień root na serwerze VPS i nie zadziała w środowisku podglądu.")}</p>
                </div>

                <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                  <input type="checkbox" checked={net.vpn_enabled} data-testid="vpn-toggle"
                    onChange={(e) => setNet({ ...net, vpn_enabled: e.target.checked })} className="accent-cyan-500" />
                  {t("Włącz konfigurację VPN")}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <select value={net.vpn_type} onChange={(e) => setNet({ ...net, vpn_type: e.target.value })} data-testid="vpn-type" className={input}>
                    <option value="direct">{t("Bezpośrednio (ta sama podsieć — bez VPN)")}</option>
                    <option value="wireguard">WireGuard</option>
                    <option value="openvpn">OpenVPN</option>
                    <option value="ipsec">IPsec</option>
                  </select>
                  <input value={net.vpn_host} onChange={(e) => setNet({ ...net, vpn_host: e.target.value })} placeholder={t("Host VPN")} data-testid="vpn-host" className={`${input} col-span-2`} />
                  <input type="number" value={net.vpn_port} onChange={(e) => setNet({ ...net, vpn_port: e.target.value })} placeholder={t("Port VPN")} data-testid="vpn-port" className={input} />
                  <input type="password" value={vpnKey} onChange={(e) => setVpnKey(e.target.value)}
                    placeholder={settings.vpn_key_set ? t("Klucz / certyfikat VPN") + " ••••" : t("Klucz / certyfikat VPN")}
                    data-testid="vpn-key" className={`${input} col-span-2`} autoComplete="off" />
                </div>
                <div className="mt-2">
                  <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Wgraj plik .ovpn (lub zaznacz naraz wszystkie: .ovpn + ca.crt + cert + .key — zostaną scalone)")}</label>
                  <input type="file" multiple accept=".ovpn,.conf,.key,.pem,.crt,.cer,.txt" data-testid="vpn-key-file"
                    onChange={handleVpnFile}
                    className="w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-700 file:text-slate-100 file:cursor-pointer" />
                  {vpnFileName && <p className="text-[11px] text-green-400 mt-1" data-testid="vpn-file-name">{t("Wczytano plik")}: {vpnFileName} ({t("zapisz ustawienia, aby zachować")})</p>}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input value={vpnUsername} onChange={(e) => setVpnUsername(e.target.value)} placeholder={t("Nazwa użytkownika VPN (opcjonalnie)")} data-testid="vpn-username" className={input} autoComplete="off" />
                  <input type="password" value={vpnPassword} onChange={(e) => setVpnPassword(e.target.value)} placeholder={settings.vpn_password_set ? t("Hasło VPN (ustawione — puste = bez zmian)") : t("Hasło VPN (opcjonalnie)")} data-testid="vpn-password" className={input} autoComplete="off" />
                </div>

                <div className="mt-3 bg-slate-900 border border-slate-700 rounded-lg p-3" data-testid="vpn-connection">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-300">{t("Połączenie VPN (serwer)")}:</span>
                    {vpnStatus ? (
                      vpnStatus.connected
                        ? <span className="flex items-center gap-1.5 text-xs rounded-full border border-green-500/50 bg-green-500/15 text-green-300 px-2.5 py-1" data-testid="vpn-status"><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> {t("Połączono")}</span>
                        : vpnStatus.running
                          ? <span className="flex items-center gap-1.5 text-xs rounded-full border border-yellow-500/50 bg-yellow-500/15 text-yellow-300 px-2.5 py-1" data-testid="vpn-status"><span className="w-2 h-2 rounded-full bg-yellow-400" /> {t("Łączenie…")}</span>
                          : <span className="flex items-center gap-1.5 text-xs rounded-full border border-slate-600 bg-slate-700/40 text-slate-300 px-2.5 py-1" data-testid="vpn-status"><span className="w-2 h-2 rounded-full bg-slate-500" /> {t("Rozłączono")}</span>
                    ) : <span className="text-xs text-slate-500" data-testid="vpn-status">{t("nieznany")}</span>}
                    {vpnStatus && !vpnStatus.installed && <span className="text-[11px] text-red-400">{t("OpenVPN nie zainstalowany na serwerze")}</span>}
                    <button onClick={loadVpnStatus} data-testid="vpn-refresh" className="ml-auto text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1">{t("Odśwież")}</button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => vpnAction("connect")} disabled={vpnBusy || (vpnStatus && vpnStatus.connected)} data-testid="vpn-connect"
                      className="bg-green-700 hover:bg-green-600 disabled:opacity-50 rounded px-4 py-2 text-sm flex items-center gap-1.5">
                      {vpnBusy ? <Loader2 size={14} className="animate-spin" /> : <Router size={14} />} {t("Połącz")}</button>
                    <button onClick={() => vpnAction("disconnect")} disabled={vpnBusy || (vpnStatus && !vpnStatus.running)} data-testid="vpn-disconnect"
                      className="bg-red-900/50 hover:bg-red-900/70 text-red-200 disabled:opacity-50 rounded px-4 py-2 text-sm flex items-center gap-1.5">
                      <X size={14} /> {t("Rozłącz")}</button>
                  </div>
                  {vpnStatus?.log_tail && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-slate-500 cursor-pointer">{t("Pokaż log połączenia")}</summary>
                      <pre className="text-[10px] text-slate-400 bg-slate-950 rounded p-2 mt-1 max-h-40 overflow-auto whitespace-pre-wrap" data-testid="vpn-log">{vpnStatus.log_tail}</pre>
                    </details>
                  )}
                  <p className="text-[11px] text-slate-500 mt-2">{t("Zapisz ustawienia po wgraniu pliku .ovpn, potem kliknij „Połącz”. Tunel zestawiany jest na serwerze (wymaga zainstalowanego OpenVPN i uprawnień root — działa na VPS, nie w podglądzie).")}</p>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">{t("Parametry VPN służą do dokumentacji dostępu do urządzeń w sieci klienta. Tunel zestawiany jest po stronie systemu operacyjnego serwera.")}</p>
              </Section>
            </div>
          )}

          {/* ============ WYGLĄD ============ */}
          {activeTab === "wyglad" && (
            <div className="space-y-3">
              {/* Język / Language — dostępny dla wszystkich */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4" data-testid="settings-language">
                <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><Languages size={16} className="text-cyan-400" /> {t("Język / Language")}</h3>
                <LanguageSwitcher variant="row" />
              </div>

              {isAdmin && (
                <>
                  {/* Kolory kabli na mapie */}
                  <Section icon={Cable} iconColor="text-blue-400" title={t("Kolory kabli na mapie")}
                    open={open === "cablecolors"} onToggle={() => toggle("cablecolors")} testid="settings-sec-cablecolors">
                    <CableColorSettings settings={settings} save={save} />
                  </Section>

                  {/* Ikony markerów na mapie */}
                  <Section icon={MapPin} iconColor="text-green-400" title={t("Ikony na mapie (mufa, słup, OLT, szafa, spliter, ONU)")}
                    open={open === "markericons"} onToggle={() => toggle("markericons")} testid="settings-sec-markericons">
                    <MarkerIconSettings settings={settings} save={save} />
                  </Section>

                  {/* Styl menu (układ nawigacji) */}
                  <Section icon={LayoutDashboard} iconColor="text-cyan-400" title={t("Styl menu (układ nawigacji)")}
                    testid="settings-menu-style" open={open === "menustyle"} onToggle={() => toggle("menustyle")}>
                    <p className="text-xs text-slate-400 mb-3">{t("Wybierz położenie i styl głównego menu. Ustawienie zapisywane jest indywidualnie dla Twojego konta. Zmiana jest natychmiastowa.")}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="menu-style-options">
                      {[["left", t("Boczne (lewe)"), "▏≡"], ["right", t("Boczne (prawe)"), "≡▕"], ["top", t("Górne (poziome)"), "▔≡"], ["dock", t("Dock (macOS, dolne)"), "◍◍◍"]].map(([v, label, glyph]) => {
                        const current = authUser?.menu_style || settings.menu_style || "left";
                        const activeStyle = current === v;
                        return (
                          <button key={v} type="button" data-testid={`menu-style-${v}`}
                            onClick={async () => {
                              try {
                                const { data } = await api.put("/auth/me/preferences", { menu_style: v });
                                if (setUser) setUser((u) => ({ ...(u || {}), ...data }));
                                toast.success(t("Styl menu zapisany"));
                              } catch { toast.error(t("Nie udało się zapisać")); }
                            }}
                            className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-3 text-xs transition-colors ${activeStyle ? "border-cyan-500 bg-cyan-500/10 text-cyan-200" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}>
                            <span className="text-lg leading-none tracking-widest">{glyph}</span>
                            <span className="text-center leading-tight">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </Section>

                  {/* Dostosowanie menu (pokaż/ukryj, przenieś, własna grupa) */}
                  <MenuCustomizer currentUser={authUser} setUser={setUser}
                    open={open === "menucust"} onToggle={() => toggle("menucust")} />

                  {/* Marka i wersja (branding) */}
                  <BrandingSettings t={t} input={input} isAdmin={isAdmin} settings={settings}
                    brand={brand} setBrand={setBrand}
                    open={open === "brand"} onToggle={() => toggle("brand")} />

                  {isAdmin && (
                    <SystemStatus t={t} open={open === "sysstatus"} onToggle={() => toggle("sysstatus")} />
                  )}
                  {isAdmin && (
                    <SecretRotation t={t} open={open === "secrets"} onToggle={() => toggle("secrets")} />
                  )}

                  {/* Mapa / kafle */}
                  <MapSettings t={t} input={input} mapCfg={mapCfg} setMapCfg={setMapCfg}
                    applyMapProvider={applyMapProvider}
                    open={open === "map"} onToggle={() => toggle("map")} />

                  {/* Kafelki */}
                  <Section icon={LayoutDashboard} title={t("Kafelki w panelu statystyk")}
                    open={open === "widgets"} onToggle={() => toggle("widgets")} testid="settings-sec-widgets">
                    <div className="grid grid-cols-2 gap-2">
                      {WIDGET_KEYS.map((k) => (
                        <label key={k} data-testid={`widget-toggle-${k}`} className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded px-2.5 py-2 text-sm cursor-pointer hover:border-slate-600">
                          <input type="checkbox" checked={widgets.includes(k)} onChange={() => toggleWidget(k)} className="accent-cyan-500" />
                          {t(WIDGET_LABELS[k])}
                        </label>
                      ))}
                    </div>
                  </Section>
                </>
              )}
            </div>
          )}

          {/* ============ LICENCJE (tylko serwer MASTER) ============ */}
          {activeTab === "licencje" && isAdmin && isMaster && (
            <div className="space-y-3">
              <div className="bg-cyan-950/30 border border-cyan-800/50 rounded-lg px-4 py-3" data-testid="licenses-master-note">
                <div className="flex items-center gap-2 text-cyan-200 text-sm font-semibold"><KeyRound size={15} /> {t("Serwer MASTER — wydawca licencji")}</div>
                <p className="text-[12px] text-slate-400 mt-1">{t("Tutaj generujesz i zarządzasz licencjami dla swoich klientów. Klucz prywatny wydawcy jest skonfigurowany na tym serwerze.")}</p>
              </div>
              <ActivationCodes t={t} open={open === "actcodes"} onToggle={() => toggle("actcodes")} />
              <LicenseGenerator t={t} input={input} open={open === "licgen"} onToggle={() => toggle("licgen")} />
              <LicenseClients t={t} open={open === "licclients"} onToggle={() => toggle("licclients")} />
              <LicenseMonitor t={t} open={open === "licmonitor"} onToggle={() => toggle("licmonitor")} />
            </div>
          )}

          {/* ============ SYSTEM ============ */}
          {activeTab === "system" && (
            <div className="space-y-3">
              {/* Zmiana hasła — dla wszystkich */}
              <Section icon={KeyRound} iconColor="text-yellow-400" title={t("Zmiana własnego hasła")}
                open={open === "pwd"} onToggle={() => toggle("pwd")} testid="settings-sec-pwd">
                <div className="space-y-2">
                  <input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                    placeholder={t("Obecne hasło")} data-testid="pwd-current" className={input} />
                  <input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                    placeholder={t("Nowe hasło (min. 6 znaków)")} data-testid="pwd-new" className={input} />
                  <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                    placeholder={t("Powtórz nowe hasło")} data-testid="pwd-confirm" className={input} />
                  <button onClick={changePassword} disabled={pwdBusy} data-testid="pwd-submit"
                    className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                    {pwdBusy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("Zmień hasło")}
                  </button>
                </div>
              </Section>

              <Section icon={Keyboard} iconColor="text-cyan-400" title={t("Skróty klawiszowe")}
                open={open === "shortcuts"} onToggle={() => toggle("shortcuts")} testid="settings-sec-shortcuts">
                <ShortcutSettings t={t} />
              </Section>

              {isAdmin && (
                <>
                  <BackupSettings
                    t={t} input={input} settings={settings}
                    open={open === "backup"} onToggle={() => toggle("backup")}
                    autoBk={autoBk} setAutoBk={setAutoBk} extBk={extBk} setExtBk={setExtBk}
                    s3Secret={s3Secret} setS3Secret={setS3Secret} ftpPass={ftpPass} setFtpPass={setFtpPass}
                    extTestBusy={extTestBusy} testExternal={testExternal} smtpConfigured={smtpConfigured}
                    backupList={backupList} backupRunBusy={backupRunBusy} runBackupNow={runBackupNow}
                    restoreSaved={restoreSaved} downloadSaved={downloadSaved} deleteSaved={deleteSaved}
                    restoreRef={restoreRef} restoreBusy={restoreBusy} doRestore={doRestore} downloadBackup={downloadBackup}
                    updateRef={updateRef} updateBusy={updateBusy} doUpdate={doUpdate} />

                  {/* Serwer aktualizacji */}
                  <Section icon={DownloadCloud} iconColor="text-emerald-400" title={t("Serwer aktualizacji")}
                    open={open === "update"} onToggle={() => toggle("update")} testid="settings-sec-update">
                    <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Adres serwera aktualizacji")}</label>
                    <input value={upd.update_server} onChange={(e) => setUpd({ ...upd, update_server: e.target.value })}
                      placeholder="https://twoj-serwer.pl" data-testid="update-server-url" className={input} />
                    <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
                      <input type="checkbox" checked={upd.auto_update_enabled} onChange={(e) => setUpd({ ...upd, auto_update_enabled: e.target.checked })} data-testid="update-auto-toggle" />
                      {t("Włącz automatyczną aktualizację (sprawdzanie co 6 h)")}
                    </label>
                    <div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-700 pt-3">
                      <div className="text-[11px] text-slate-400">{t("Aktualizacja przez prywatny rejestr Docker (dla instalacji kontenerowych)")}</div>
                      <div>
                        <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Obraz rejestru (ścieżka)")}</label>
                        <input value={upd.docker_image} onChange={(e) => setUpd({ ...upd, docker_image: e.target.value })}
                          placeholder="fiberpass.pl/fiberpass-client" data-testid="update-docker-image" className={input} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Tag / kanał")}</label>
                        <input value={upd.docker_tag} onChange={(e) => setUpd({ ...upd, docker_tag: e.target.value })}
                          placeholder="stable" data-testid="update-docker-tag" className={input} />
                      </div>
                    </div>
                    {updHist.length > 0 && (
                      <div className="mt-3 border-t border-slate-700 pt-3" data-testid="update-history">
                        <div className="text-xs font-semibold text-slate-300 mb-1.5">{t("Historia aktualizacji")}</div>
                        <div className="space-y-1 max-h-40 overflow-auto pr-1">
                          {updHist.map((h, i) => (
                            <div key={i} data-testid={`update-history-row-${i}`}
                              className="flex items-center justify-between text-[10px] text-slate-400 bg-slate-900/50 rounded px-2 py-1">
                              <span className="text-slate-200 font-mono">v{h.version}</span>
                              <span>{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                              <span className="text-slate-500">{h.source === "docker" ? t("obraz") : t("system")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-2">{t("Bieżąca wersja")}: <b className="text-slate-200" data-testid="update-current">{updInfo?.current || "—"}</b>
                      {updInfo?.latest ? <> · {t("na serwerze")}: <b className={updInfo.update_available ? "text-emerald-400" : "text-slate-200"}>{updInfo.latest}</b></> : null}</div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button onClick={saveUpdate} disabled={updBusy} data-testid="update-save" className="bg-cyan-600 hover:bg-cyan-500 rounded px-3 py-2 text-sm disabled:opacity-50">{t("Zapisz")}</button>
                      <button onClick={checkUpdate} disabled={updBusy} data-testid="update-check" className="bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm disabled:opacity-50 flex items-center gap-1"><RefreshCw size={14} /> {t("Sprawdź aktualizacje")}</button>
                      <button onClick={applyUpdate} disabled={updBusy || !updInfo?.update_available} data-testid="update-apply" className="bg-emerald-700 hover:bg-emerald-600 rounded px-3 py-2 text-sm disabled:opacity-40 flex items-center gap-1"><DownloadCloud size={14} /> {t("Aktualizuj teraz")}</button>
                    </div>
                    <div className="mt-3 border-t border-slate-700 pt-3">
                      <div className="text-xs font-semibold text-slate-300 mb-1.5">{t("Wgraj aktualizację z pliku (ZIP) — przez panel WWW")}</div>
                      <label className={`inline-flex items-center gap-2 rounded bg-emerald-700 hover:bg-emerald-600 px-3 py-2 text-sm cursor-pointer ${uplBusy ? "opacity-50 pointer-events-none" : ""}`}>
                        <Upload size={14} /> {uplBusy ? t("Wgrywanie i budowa…") : t("Wybierz plik fiberpass-full.zip")}
                        <input type="file" accept=".zip" className="hidden" disabled={uplBusy} onChange={uploadUpdate} data-testid="update-upload-input" />
                      </label>
                      <p className="text-[11px] text-slate-500 mt-1.5">{t("Wgraj pobraną paczkę tutaj — system sam ją rozpakuje, przebuduje aplikację i zrestartuje usługi. Baza, klucze i zdjęcia zostają nienaruszone.")}</p>
                    </div>
                    {isMaster && <PublishRelease />}
                    <p className="text-[11px] text-slate-500 mt-2">{t("Podaj adres serwera, z którego pobierane są aktualizacje (np. Twój główny serwer FiberPass). Po zakończeniu aktualizacji aplikacja pokaże powiadomienie i odświeży się automatycznie.")}</p>
                  </Section>

                  {/* Klient desktopowy (Windows) */}
                  <Section icon={Monitor} iconColor="text-sky-400" title={t("Klient desktopowy (Windows)")}
                    open={open === "desktop"} onToggle={() => toggle("desktop")} testid="settings-sec-desktop">
                    <div className="text-[11px] text-slate-400 mb-2">
                      {t("Opublikowana wersja")}: <b className="text-slate-200" data-testid="ds-current-version">{dsRel?.version || "—"}</b>
                      {dsRel?.installer_present
                        ? <span className="text-emerald-400"> · {t("instalator wgrany")} ({Math.round((dsRel.installer_size || 0) / 1048576)} MB)</span>
                        : <span className="text-amber-400"> · {t("brak instalatora na serwerze")}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <a href={`${API}/download/desktop-windows?auth=${encodeURIComponent(dlToken())}`} target="_blank" rel="noreferrer" data-testid="ds-dl-portable"
                        className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1"><DownloadCloud size={13} /> {t("Wersja portable (.zip)")}</a>
                      {dsRel?.installer_present && (
                        <a href={`${API}/download/desktop-installer?auth=${encodeURIComponent(dlToken())}`} target="_blank" rel="noreferrer" data-testid="ds-dl-installer"
                          className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1"><DownloadCloud size={13} /> {t("Instalator (.exe)")}</a>
                      )}
                    </div>
                    <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Numer wersji")}</label>
                    <input value={dsVer} onChange={(e) => setDsVer(e.target.value)} placeholder="np. 1.0.1" data-testid="ds-version" className={input} />
                    <label className="text-xs font-semibold text-slate-300 mb-1 mt-3 block">{t("Opis zmian (dla użytkownika)")}</label>
                    <textarea value={dsNotes} onChange={(e) => setDsNotes(e.target.value)} rows={2} data-testid="ds-notes"
                      className={`${input} resize-none`} placeholder={t("Co nowego w tej wersji…")} />
                    <label className="text-xs font-semibold text-slate-300 mb-1 mt-3 block">{t("Podpis instalatora (zawartość pliku .sig od producenta)")}</label>
                    <textarea value={dsSig} onChange={(e) => setDsSig(e.target.value)} rows={2} data-testid="ds-signature"
                      className={`${input} resize-none font-mono text-[11px]`} placeholder="FPMAN1.…" />
                    <div className="mt-3">
                      <label className={`inline-flex items-center gap-2 rounded bg-sky-700 hover:bg-sky-600 px-3 py-2 text-sm cursor-pointer ${dsBusy ? "opacity-50 pointer-events-none" : ""}`}>
                        <Upload size={14} /> {dsFile ? dsFile.name : t("Wybierz plik FiberPass-Setup.exe")}
                        <input type="file" accept=".exe" className="hidden" disabled={dsBusy} data-testid="ds-file-input"
                          onChange={(e) => { setDsFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                      </label>
                    </div>
                    {dsBusy && dsProgress > 0 && (
                      <div className="mt-2" data-testid="ds-progress">
                        <div className="h-2 bg-slate-700 rounded overflow-hidden"><div className="h-full bg-sky-500 transition-all" style={{ width: `${dsProgress}%` }} /></div>
                        <div className="text-[11px] text-slate-400 mt-1">{t("Wgrywanie")}: {dsProgress}%</div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button onClick={publishInstaller} disabled={dsBusy || !dsFile || !dsVer.trim()} data-testid="ds-publish"
                        className="bg-emerald-700 hover:bg-emerald-600 rounded px-3 py-2 text-sm disabled:opacity-40 flex items-center gap-1">
                        {dsBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("Wgraj i opublikuj")}
                      </button>
                      <button onClick={saveVersionOnly} disabled={dsBusy || !dsVer.trim()} data-testid="ds-save-version"
                        className="bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm disabled:opacity-40 flex items-center gap-1"><Save size={14} /> {t("Zapisz tylko wersję/opis")}</button>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">{t("Po opublikowaniu nowszej wersji klienci desktopowi pobiorą ją automatycznie przy następnym uruchomieniu.")}</p>
                  </Section>

                  {/* Podgląd wiadomości monterów */}
                  <Section icon={MessageSquare} iconColor="text-cyan-400" title={t("Wiadomości monterów (podgląd)")}
                    open={open === "chatadmin"} onToggle={() => toggle("chatadmin")} testid="settings-sec-chatadmin">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="max-h-64 overflow-y-auto space-y-1" data-testid="chatadmin-threads">
                        {chatThreads.length === 0 && <div className="text-xs text-slate-500">{t("Brak rozmów.")}</div>}
                        {chatThreads.map((th) => (
                          <button key={th.id} onClick={() => openChatThread(th.id)} data-testid={`chatadmin-thread-${th.id}`}
                            className={`w-full text-left rounded border px-2 py-1.5 text-xs ${chatSel === th.id ? "border-cyan-600 bg-slate-800" : "border-slate-700 bg-slate-900"}`}>
                            <div className="font-semibold truncate">{th.name}</div>
                            <div className="text-[10px] text-slate-400">{th.count} {t("wiad.")}{th.deleted_count > 0 && <span className="text-red-400"> · {th.deleted_count} {t("usuniętych")}</span>}</div>
                          </button>
                        ))}
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-1 bg-slate-900 rounded border border-slate-700 p-2" data-testid="chatadmin-messages">
                        {!chatSel && <div className="text-xs text-slate-500">{t("Wybierz rozmowę.")}</div>}
                        {chatMsgs.map((m) => (
                          <div key={m.id} className={`text-xs rounded px-2 py-1 ${m.deleted ? "bg-red-900/20 border border-red-800/40" : "bg-slate-800"}`}>
                            <div className="text-[10px] text-cyan-300 flex justify-between gap-2"><span className="truncate">{m.sender_name}</span><span className="text-slate-500 shrink-0">{(m.ts || "").slice(0, 16).replace("T", " ")}</span></div>
                            <div className="whitespace-pre-wrap break-words text-slate-200">{m.text || "—"}</div>
                            {m.deleted && <div className="text-[9px] text-red-400 mt-0.5">{t("Usunięto przez")}: {m.deleted_by_name} ({(m.deleted_at || "").slice(0, 16).replace("T", " ")})</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={loadChatThreads} data-testid="chatadmin-refresh" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1"><RefreshCw size={12} /> {t("Odśwież")}</button>
                      <p className="text-[11px] text-slate-500">{t("Kopie wszystkich wiadomości (także usuniętych) są przechowywane na serwerze — widoczne tylko dla administratora.")}</p>
                    </div>
                  </Section>

                  {/* Asystent AI */}
                  <Section icon={Activity} iconColor="text-pink-400" title={t("Asystent AI — własny klucz")}
                    open={open === "ai"} onToggle={() => toggle("ai")} testid="settings-sec-ai">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Dostawca")}</label>
                        <select value={ai.ai_provider} onChange={(e) => setAi({ ...ai, ai_provider: e.target.value })} data-testid="ai-provider" className={input}>
                          <option value="openai">OpenAI</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Model</label>
                        <input value={ai.ai_model} onChange={(e) => setAi({ ...ai, ai_model: e.target.value })} placeholder="np. gpt-4o-mini" data-testid="ai-model" className={input} />
                      </div>
                    </div>
                    <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Klucz API")} {settings.ai_api_key_set && <span className="text-green-400">{t("(ustawiony)")}</span>}</label>
                    <input type="password" value={aiKey} onChange={(e) => setAiKey(e.target.value)}
                      placeholder={settings.ai_api_key_set ? "•••••••• (zostaw puste, aby nie zmieniać)" : "wklej klucz API (sk-...)"}
                      data-testid="ai-api-key" className={input} autoComplete="off" />
                    <p className="text-[11px] text-slate-500 mt-2">{t("Gdy podasz własny klucz, Asystent AI korzysta z Twojego konta OpenAI. Puste pole = bez zmian.")}</p>
                  </Section>
                </>
              )}
            </div>
          )}

          {isAdmin && (
            <button onClick={saveConfig} disabled={cfgBusy} data-testid="settings-save"
              className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 sticky bottom-0">
              {cfgBusy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("Zapisz ustawienia")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
